// src/pages/agent/LeadDetail.js
// The workhorse screen, redesigned as a vertical deal-flow timeline.
// Each step pairs a teaching breadcrumb (pulled from the training
// transcript) with the working surface for that step.
//
// Steps (admin/agent view):
//   ① Property Details
//   ② Talk to Seller (4 Pillars)
//   ③ Comps (RentCast or manual)
//   ④ ARV (auto-averaged from comps, override-able)
//   ⑤ MAO (ARV × 70% − Repairs − Fee)
//   ⑥ Notes & Call Log
//   ⑦ SMS Thread
//   ⑧ Contracts
//   ⑨ AI Assistant
//
// Lead submitters get a stripped view (Property + Comps only) — see
// SubmissionDetail.js. The DealTimeline component is exported so both
// views can reuse it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, deleteDoc, arrayUnion, arrayRemove, serverTimestamp, Timestamp, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { zillowUrl, extractZillowUrls, addressFromZillowUrl } from '../../lib/zillowUrl';
import { runWholesaleCalc, REPAIR_TIERS, DEFAULT_LOCATION_PCT, fmtMoney, computeAutoArv } from '../../lib/calc';
import { renderContractHtml } from '../../lib/contracts/render';
import { CONTRACT_META } from '../../lib/contracts/templates';
import { SMS_TEMPLATES, interpolateTemplate, ownerFirstName, streetNameOnly } from '../../lib/smsTemplates';
import AddressAutocomplete from '../../components/agent/AddressAutocomplete';
import Timeline from '../../components/agent/Timeline';
import {
  PageHeader, Card, Btn, Field, Input, Select, TextArea,
  BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, BORDER, MID, LIGHT,
  StatusPill, Pill
} from '../../components/agent/UI';

const STATUSES = ['New', 'Contacted', 'Negotiating', 'Under Contract', 'Assigned', 'Closed', 'Dead'];
const NOTE_TYPES = ['Initial Call', 'Follow Up', 'Voicemail', 'Renegotiation', 'Objection', 'SMS', 'Email', 'Other'];
const CONTRACT_TYPES = [
  { id: 'psa',         label: 'Purchase & Sale Agreement' },
  { id: 'assignment',  label: 'Assignment of Contract' },
  { id: 'novation',    label: 'Novation Agreement' },
  { id: 'cancellation',label: 'Cancellation of Contract' },
  { id: 'jv',          label: 'JV Partnership Agreement' }
];

export default function LeadDetail() {
  const { leadId } = useParams();
  const { profile } = useAgentAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [ownerUid, setOwnerUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClose, setShowClose] = useState(false);

  // Resolve which pipeline this lead lives under. Admin: their own. Contract
  // agent: the inviting admin's pipeline (or the first active admin as
  // fallback). The Firestore read still gates on rules, so unauthorized
  // reads fail at the DB layer.
  useEffect(() => {
    if (!profile?.uid || !leadId) return;
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      let path = profile.uid;
      if (profile.role !== 'admin') {
        // Contract agent or lead submitter — find the pipeline owner.
        path = profile.invitedByUid || null;
        if (!path) {
          try {
            const aSnap = await getDocs(query(
              collection(db, 'agents'),
              where('role', '==', 'admin'),
              where('status', '==', 'active'),
              limit(1)
            ));
            if (!aSnap.empty) path = aSnap.docs[0].id;
          } catch {}
        }
        if (!path) path = profile.uid; // last-ditch fallback
      }
      if (cancelled) return;
      setOwnerUid(path);
      const ref = doc(db, 'agents', path, 'leads', leadId);
      unsub = onSnapshot(ref, snap => {
        if (snap.exists()) setLead({ id: snap.id, ...snap.data() });
        else setLead(null);
        setLoading(false);
      }, (err) => {
        console.error('Lead read denied:', err.message);
        setLead(null); setLoading(false);
      });
    })();
    return () => { cancelled = true; unsub(); };
  }, [profile?.uid, profile?.role, profile?.invitedByUid, leadId]);

  const update = async (patch) => {
    if (!ownerUid) return;
    const ref = doc(db, 'agents', ownerUid, 'leads', leadId);
    await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
  };

  // Permanently delete this lead. Used to clear out test leads. Unlike
  // "Close" (which keeps the record), this removes the document for good.
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');
  const deleteLead = async () => {
    if (!ownerUid) return;
    setDeleting(true); setDeleteErr('');
    try {
      await deleteDoc(doc(db, 'agents', ownerUid, 'leads', leadId));
      navigate('/agent/leads');
    } catch (e) {
      setDeleteErr(e.message || 'Delete failed.');
      setDeleting(false);
    }
  };

  // Mark this lead's INBOUND SMS as read when the agent opens it: stamp
  // lastInboundReadAt and zero the unread counter. This is what clears the
  // SMS bell + the per-conversation "new" dot on the Messages screen. We
  // write directly (no updatedAt bump) so it doesn't reorder the list, and
  // guard with a ref so a single open writes at most once per new reply.
  const markedReadRef = useRef(null);
  useEffect(() => {
    if (!lead || !ownerUid || !leadId) return;
    const lastIn = (lead.smsThread || []).filter(m => m.direction === 'in').slice(-1)[0];
    const lastMs = lastIn?.sentAt?.toMillis?.() || 0;
    if (!lastMs) return;
    const readMs = lead.lastInboundReadAt?.toMillis?.() || 0;
    if (readMs >= lastMs || markedReadRef.current === lastMs) return;
    markedReadRef.current = lastMs;
    updateDoc(doc(db, 'agents', ownerUid, 'leads', leadId), {
      lastInboundReadAt: Timestamp.now(),
      smsUnreadCount: 0
    }).catch(() => {});
  }, [lead, ownerUid, leadId]);

  // Acknowledge outbound delivery failures when the lead is opened — this
  // clears the red ⚠️ count on the SMS nav. A later failure (newer
  // lastSmsFailureAt) re-lights it. The per-message "Not delivered" badge in
  // the thread stays as a permanent record.
  const ackedFailRef = useRef(null);
  useEffect(() => {
    if (!lead || !ownerUid || !leadId) return;
    const failMs = lead.lastSmsFailureAt?.toMillis?.() || 0;
    if (!failMs) return;
    const ackMs = lead.smsFailuresAckedAt?.toMillis?.() || 0;
    if (ackMs >= failMs || ackedFailRef.current === failMs) return;
    ackedFailRef.current = failMs;
    updateDoc(doc(db, 'agents', ownerUid, 'leads', leadId), {
      smsFailuresAckedAt: Timestamp.now()
    }).catch(() => {});
  }, [lead, ownerUid, leadId]);

  if (loading) return <div style={{ padding: 40, color: LIGHT }}>Loading lead…</div>;
  if (!lead) return <div style={{ padding: 40 }}>Lead not found. <a href="/agent/leads">Back to leads</a></div>;

  // USPS-standard: "Street, City, ST ZIP" (no comma between state and zip).
  // RentCast and geocoders reject the comma-between-state-and-zip variant.
  const stateZip = [lead.address?.state, lead.address?.zip].filter(Boolean).join(' ');
  const fullAddr = [lead.address?.line1, lead.address?.city, stateZip].filter(Boolean).join(', ');
  const isDead = lead.status === 'Dead' || lead.status === 'Closed';

  return (
    <>
      <PageHeader
        eyebrow="Lead"
        title={lead.address?.line1 || '(no address)'}
        sub={[lead.address?.city, lead.address?.state, lead.address?.zip].filter(Boolean).join(', ')}
        right={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={() => navigate('/agent/leads')}>← All leads</Btn>
            <Select value={lead.status || 'New'} onChange={e => update({ status: e.target.value })} style={{ minWidth: 160 }}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </Select>
            {!isDead && (
              <Btn variant="danger" onClick={() => setShowClose(true)} title="Close this lead — seller declined or not interested">
                ✕ Close lead
              </Btn>
            )}
            <Btn variant="ghost" onClick={() => setShowDelete(true)} title="Permanently delete this lead (e.g. a test lead)"
              style={{ color: '#991B1B', borderColor: '#FCA5A5' }}>
              🗑 Delete
            </Btn>
          </div>
        }
      />

      {showDelete && (
        <div style={delModal.overlay} onClick={() => !deleting && setShowDelete(false)}>
          <div style={delModal.card} onClick={e => e.stopPropagation()}>
            <div style={delModal.eyebrow}>🗑 Delete lead permanently</div>
            <h3 style={delModal.title}>Delete this lead?</h3>
            <p style={delModal.body}>
              This permanently removes <strong>{lead.address?.line1 || (lead.owners?.[0]?.name) || (lead.owners?.[0]?.phone) || 'this lead'}</strong>
              {' '}and its notes, contracts, and SMS history. This <strong>can't be undone</strong>. To just mark it
              dead, use <em>Close lead</em> instead.
            </p>
            {deleteErr && <div style={delModal.err}>{deleteErr}</div>}
            <div style={delModal.actions}>
              <Btn variant="ghost" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</Btn>
              <Btn variant="danger" onClick={deleteLead} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {isDead && lead.closeReason && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px', marginBottom: 18, color: '#991B1B', fontSize: 13 }}>
          <strong>Closed.</strong> {lead.closeReason}{lead.closedAt?.toDate ? ` · ${lead.closedAt.toDate().toLocaleDateString()}` : ''}
        </div>
      )}

      {/* Lead Agent attribution — who submitted this and when. Always
          visible at the top of the lead so the team knows who to credit. */}
      {lead.submittedBy && (
        <div style={{
          background: GOLD_PALE,
          border: '1px solid rgba(201,168,76,0.4)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 18,
          fontSize: 12,
          color: BURG_DK,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: BURG }}>
            Lead Agent
          </span>
          <strong style={{ color: '#1C1C1C' }}>{lead.submittedBy.name || 'Unknown'}</strong>
          {lead.submittedBy.phone && <span style={{ color: MID, fontFamily: 'monospace' }}>· {lead.submittedBy.phone}</span>}
          {lead.submittedBy.email && <span style={{ color: MID }}>· {lead.submittedBy.email}</span>}
          {lead.submittedBy.source === 'public_form' && <Pill color="gold">Public form</Pill>}
          <span style={{ marginLeft: 'auto', color: MID, fontStyle: 'italic' }}>
            {lead.submittedBy.submittedAt?.toDate
              ? `Submitted ${lead.submittedBy.submittedAt.toDate().toLocaleString()}`
              : (lead.createdAt?.toDate
                  ? `Created ${lead.createdAt.toDate().toLocaleString()}`
                  : '')}
          </span>
        </div>
      )}

      <div className="homeiqa-lead-shell" style={shell.grid}>
        <div style={shell.timelineCol}>
          <JvPartnerViewCard lead={lead} onUpdate={update} />
          <DealTimeline
            lead={lead}
            update={update}
            profile={profile}
            fullAddr={fullAddr}
            viewMode="full"
          />
        </div>
        <aside style={shell.sidebarCol}>
          <div className="homeiqa-lead-sidebar-sticky" style={shell.sidebarSticky}>
            {/* Pinned banner — always visible at the top of the sidebar
                even while the SMS thread + Notes scroll below. */}
            <div style={shell.outreachBanner}>
              <div style={shell.outreachHead}>
                <span style={shell.outreachIcon}>📱</span>
                <div>
                  <div style={shell.outreachTitle}>Initial outreach is by SMS</div>
                  <div style={shell.outreachSub}>Text first, then call. Quick-send templates below.</div>
                </div>
              </div>
              <div style={shell.outreachNumberBlock}>
                <div style={shell.outreachNumberLabel}>Your outbound number — give this to callers</div>
                <a href="tel:+14149466347" style={shell.outreachNumber}>(414) 946-6347</a>
              </div>
            </div>
            {/* Scrollable area — SMS thread + Notes feed scroll
                independently of the pinned banner above. */}
            <div style={shell.sidebarScroll}>
              <div style={{ marginBottom: 14 }}>
                <SmsPanel lead={lead} agentUid={profile.uid} agent={profile} />
              </div>
              <NotesPanel lead={lead} onUpdate={update} agent={profile} />
            </div>
          </div>
        </aside>
      </div>


      {showClose && (
        <CloseLeadModal
          onClose={() => setShowClose(false)}
          onConfirm={async (reason, finalStatus) => {
            const closeNote = {
              type: 'Other',
              body: `🔒 Lead closed (${finalStatus}). Reason: ${reason}`,
              createdAt: Timestamp.now(),
              author: { uid: profile.uid, name: profile.name || profile.email }
            };
            await update({
              status: finalStatus,
              closeReason: reason,
              closedAt: Timestamp.now(),
              closedBy: { uid: profile.uid, name: profile.name || profile.email },
              notes: arrayUnion(closeNote)
            });
            setShowClose(false);
          }}
        />
      )}
    </>
  );
}

// Confirmation modal for closing/killing a lead. Captures a reason
// and lets the agent pick "Dead" (truly gone) vs. "Closed" (handled,
// archive). Reason is stored on the lead doc for reporting.
function CloseLeadModal({ onClose, onConfirm }) {
  const REASONS = [
    'Seller decided not to sell',
    'Seller already has another buyer / agent',
    'Seller wants too much (above MAO)',
    'House is in too poor / too good condition',
    'No-contact after 5 attempts',
    'Wrong number / not the owner',
    'Other'
  ];
  const [reason, setReason] = useState(REASONS[0]);
  const [other, setOther] = useState('');
  const [finalStatus, setFinalStatus] = useState('Dead');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const note = reason === 'Other' ? (other.trim() || 'Other') : reason;
    setBusy(true);
    try { await onConfirm(note, finalStatus); }
    finally { setBusy(false); }
  };

  return (
    <div style={mod.backdrop} onClick={onClose}>
      <div style={mod.panel} onClick={e => e.stopPropagation()}>
        <div style={mod.head}>
          <div style={mod.title}>Close this lead?</div>
          <button onClick={onClose} style={mod.x} aria-label="Close">×</button>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ fontSize: 13, color: MID, lineHeight: 1.6, margin: '0 0 16px' }}>
            Closing the lead removes it from your active pipeline. You can always re-open it from the lead list.
          </p>
          <Field label="Reason for closing">
            <Select value={reason} onChange={e => setReason(e.target.value)}>
              {REASONS.map(r => <option key={r}>{r}</option>)}
            </Select>
          </Field>
          {reason === 'Other' && (
            <Field label="Specify">
              <TextArea rows={2} value={other} onChange={e => setOther(e.target.value)} placeholder="What happened?" />
            </Field>
          )}
          <Field label="Final status">
            <Select value={finalStatus} onChange={e => setFinalStatus(e.target.value)}>
              <option value="Dead">Dead — not coming back</option>
              <option value="Closed">Closed — archive (handled)</option>
            </Select>
          </Field>
        </div>
        <div style={mod.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn variant="danger" onClick={submit} disabled={busy}>{busy ? 'Closing…' : `✕ Close as ${finalStatus}`}</Btn>
        </div>
      </div>
    </div>
  );
}

// Two-column shell for the lead workspace. Notes + SMS live in a sticky
// sidebar so the agent never has to scroll past the timeline to log a
// call or fire off a quick reply.
const shell = {
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 28, alignItems: 'flex-start' },
  timelineCol: { minWidth: 0 },
  sidebarCol: { minWidth: 0 },
  // Sidebar is now a plain column in the document flow — scrolls with
  // the page (not sticky / not capped to viewport height). The outreach
  // banner sits at the top so the agent always sees the outbound
  // number when they land on a lead. SMS thread + Notes feed expand
  // naturally; their internal lists scroll inside themselves only if
  // the conversation/note count overflows their own cap.
  sidebarSticky: { display: 'flex', flexDirection: 'column' },
  sidebarScroll: { display: 'flex', flexDirection: 'column' },
  outreachBanner: { background: BURG_DK, color: '#fff', borderRadius: 12, padding: '16px 18px', marginBottom: 14, border: `1px solid rgba(201,168,76,0.4)` },
  outreachHead: { display: 'flex', gap: 12, alignItems: 'center' },
  outreachIcon: { fontSize: 22, lineHeight: 1, flexShrink: 0 },
  outreachTitle: { fontFamily: "'Manrope', sans-serif", fontSize: 15, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3, letterSpacing: '-0.01em' },
  outreachNumberBlock: { marginTop: 14, paddingTop: 14, borderTop: `1px solid rgba(201,168,76,0.25)`, textAlign: 'center' },
  outreachNumberLabel: { fontSize: 11, color: GOLD, marginBottom: 6, fontWeight: 500, fontFamily: "'Inter', sans-serif" },
  outreachNumber: { fontFamily: "'Manrope', sans-serif", fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', display: 'block', textDecoration: 'none', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  outreachSub: { fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }
};
// Hide the sticky sidebar's stickiness and stack on narrow screens. Done
// via a CSS rule injected once below — inline styles can't do media queries.
if (typeof document !== 'undefined' && !document.getElementById('homeiqa-leaddetail-css')) {
  const style = document.createElement('style');
  style.id = 'homeiqa-leaddetail-css';
  style.textContent = `
    @media (max-width: 1100px) {
      .homeiqa-lead-shell { grid-template-columns: 1fr !important; }
      .homeiqa-lead-sidebar-sticky {
        position: static !important;
        height: auto !important;
      }
    }
  `;
  document.head.appendChild(style);
}

const mod = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', backdropFilter: 'blur(2px)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" },
  head: { padding: '16px 22px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: CREAM },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: BURG_DK },
  x: { background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1, color: LIGHT, cursor: 'pointer', padding: 0, width: 28 },
  footer: { padding: '14px 22px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }
};

const delModal = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', backdropFilter: 'blur(2px)', zIndex: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, padding: '24px 26px', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', border: '1px solid #FCA5A5', fontFamily: "'DM Sans', sans-serif" },
  eyebrow: { fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#991B1B', marginBottom: 8 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: BURG_DK, margin: '0 0 12px' },
  body: { fontSize: 13.5, color: MID, lineHeight: 1.6, margin: '0 0 16px' },
  err: { padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginBottom: 12 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10 }
};

// ─── Deal-flow timeline ───
// Step status drives the dot color: 'pending' → grey, 'active' → burgundy,
// 'done' → green check. The status is computed off the lead doc at render
// so the timeline reflects real progress.
//
// `viewMode` controls which steps appear:
//   'full'           → admin/agent view (all 9 steps)
//   'lead_submitter' → only Property + Comps + a handoff message
export function DealTimeline({ lead, update, profile, fullAddr, viewMode = 'full' }) {
  const isLeadSubmitter = viewMode === 'lead_submitter';
  const allComps = [...(lead.comps || []), ...(lead.manualComps || [])];
  const autoArv = computeAutoArv(lead.comps, lead.manualComps);

  // ── Helpers to compute step status ──
  const propertyDone = !!(lead.address?.line1 && lead.address?.city && (lead.propertyData?.beds || lead.propertyData?.baths || lead.propertyData?.sqft));
  const compsDone = allComps.length >= 3;
  const arvDone = !!(lead.calc?.arv || autoArv);
  const maoDone = !!(lead.calc?.results?.mao);
  const sellerCallDone = !!lead.sellerCall?.completedAt
    || !!(lead.notes || []).some(n => n.type === 'Initial Call');
  const contractsDone = !!(lead.contracts || []).some(c => c.status === 'signed');

  const steps = [];

  // ① Property Details
  steps.push({
    id: 'property',
    title: 'Property Details',
    tag: '①',
    sub: 'Address, owner, beds, baths, sqft. The foundation everything else builds on.',
    note: 'Look for outdated houses, vacant houses, properties with grandparents\' wallpaper. They\'re like that for a reason — find out what.',
    status: propertyDone ? 'done' : 'active',
    summary: lead.address?.line1 || 'No address yet',
    content: <PropertyPanel lead={lead} onUpdate={update} fullAddr={fullAddr} />
  });

  if (!isLeadSubmitter) {
    // ② Talk to Seller
    steps.push({
      id: 'seller-call',
      title: 'Talk to the Seller',
      tag: '②',
      sub: 'Get the four pillars: motivation, timeline, condition, price.',
      note: 'A good first call is under 9 minutes. Read the script — don\'t freestyle. Get the four pillars: why are they selling, when, what shape is the house in, what price will they walk away for.',
      status: sellerCallDone ? 'done' : (propertyDone ? 'active' : 'pending'),
      summary: sellerCallDone ? 'Initial call logged' : 'Not yet called',
      content: <SellerCallPanel lead={lead} onUpdate={update} agent={profile} />
    });
  }

  // ③ Comps
  steps.push({
    id: 'comps',
    title: 'Comparable Sales',
    tag: isLeadSubmitter ? '②' : '③',
    sub: 'Find 3 sold properties similar to yours, within ~1 mile, sold in the last 6 months.',
    note: 'Look at SOLD properties on Zillow, not For Sale. Aim for 3 within 1 mile, sold in the last 6 months. Don\'t let ChatGPT do your comps — go find them yourself. Start with RentCast; if no comps come back, add them manually below.',
    status: compsDone ? 'done' : (propertyDone ? 'active' : 'pending'),
    summary: allComps.length ? `${allComps.length} comp${allComps.length === 1 ? '' : 's'}${(lead.manualComps || []).length ? ' (+manual)' : ''}` : 'Not pulled yet',
    content: <CompsPanel lead={lead} fullAddr={fullAddr} onUpdate={update} canEdit />
  });

  if (!isLeadSubmitter) {
    // ④ ARV
    steps.push({
      id: 'arv',
      title: 'Set the ARV',
      tag: '④',
      sub: 'After-Repair Value — what the house sells for once it\'s fixed up.',
      note: 'ARV is just the average of 3 renovated comp properties. We pre-fill it from your comps — adjust if your gut says different.',
      status: arvDone ? 'done' : (compsDone ? 'active' : 'pending'),
      summary: lead.calc?.arv ? `ARV ${fmtMoney(lead.calc.arv)}` : autoArv ? `Suggested ${fmtMoney(autoArv)}` : 'Not set yet',
      content: <ArvPanel lead={lead} onUpdate={update} autoArv={autoArv} />
    });

    // ⑤ MAO
    steps.push({
      id: 'mao',
      title: 'Calculate MAO',
      tag: '⑤',
      sub: 'Max Allowable Offer = ARV × 70% − Repairs − Assignment Fee.',
      note: 'Always go with realistic and high repair numbers — never low to make the seller happy. Never give the seller your MAO. Start $10K–$15K below.',
      status: maoDone ? 'done' : (arvDone ? 'active' : 'pending'),
      summary: lead.calc?.results?.mao ? `MAO ${fmtMoney(lead.calc.results.mao)}` : 'Not calculated yet',
      content: <MaoPanel lead={lead} onUpdate={update} />
    });

    // ⑥ Contracts (Notes + SMS live in the sidebar, always visible)
    steps.push({
      id: 'contracts',
      title: 'Contracts',
      tag: '⑥',
      sub: 'PSA → Assignment / Novation. IQ Sign included; PDF returned.',
      note: 'Once they agree on the price, send the PSA the same day — don\'t let them sleep on it.',
      status: contractsDone ? 'done' : ((lead.contracts || []).length ? 'active' : 'pending'),
      summary: (lead.contracts || []).length ? `${(lead.contracts || []).length} sent` : 'None sent yet',
      content: <ContractsPanel lead={lead} onUpdate={update} agentUid={profile.uid} fullAddr={fullAddr} />
    });

    // ⑦ AI Assistant
    steps.push({
      id: 'ai',
      title: 'AI Assistant',
      tag: '⑦',
      sub: 'Repair estimates, ARV analysis, follow-up drafts, objection handling.',
      note: 'Use the AI for ideas — but verify the comps and numbers yourself. The AI doesn\'t walk the property.',
      status: 'pending',
      summary: 'On demand',
      content: <AiPanel lead={lead} fullAddr={fullAddr} />
    });
  }

  return (
    <>
      <Timeline steps={steps} defaultOpenIndex={0} />
      {isLeadSubmitter && (
        <Card style={{ background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, marginTop: 8 }}>
          <div style={{ fontSize: 14, color: BURG_DK, lineHeight: 1.7 }}>
            <strong>That's your part — thank you!</strong> Once your property and comps are in, the HomeIQa team takes over: setting the ARV, calculating the offer, calling the seller, and signing contracts. You'll see the status update on <strong>My Submissions</strong> as the team works it.
          </div>
        </Card>
      )}
    </>
  );
}

// Card that lets the admin override what the JV partner sees for this
// lead. When left on "Auto", the JV share page derives status + notes
// from opt-out, response, and outreach attempts. Any manual value takes
// precedence and shows on the JV dashboard instantly (via 60s polling).
const JV_STATUS_PRESETS = [
  { label: 'Auto (derive from activity)', value: '', color: '' },
  { label: 'Called', value: 'Called', color: 'gold' },
  { label: 'Left voicemail', value: 'Left voicemail', color: 'gold' },
  { label: 'Calculating ARV', value: 'Calculating ARV', color: 'blue' },
  { label: 'Negotiating', value: 'Negotiating', color: 'blue' },
  { label: 'Offer sent', value: 'Offer sent', color: 'blue' },
  { label: 'Contract sent', value: 'Contract sent', color: 'blue' },
  { label: 'Under contract', value: 'Under contract', color: 'green' },
  { label: 'Assigned to buyer', value: 'Assigned to buyer', color: 'green' },
  { label: 'Closed', value: 'Closed', color: 'green' },
  { label: 'Not interested — dead', value: 'Dead Deal · Not interested', color: 'red' },
  { label: 'Custom…', value: '__custom', color: '' }
];

function JvPartnerViewCard({ lead, onUpdate }) {
  const current = lead.jvStatusOverride || '';
  const isCustom = current && !JV_STATUS_PRESETS.some(p => p.value === current);
  const [selection, setSelection] = useState(isCustom ? '__custom' : current);
  const [customText, setCustomText] = useState(isCustom ? current : '');
  const [customColor, setCustomColor] = useState(lead.jvStatusColorOverride || 'gold');
  const [notes, setNotes] = useState(lead.jvNotesOverride || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setSelection(isCustom ? '__custom' : current);
    setCustomText(isCustom ? current : '');
    setCustomColor(lead.jvStatusColorOverride || 'gold');
    setNotes(lead.jvNotesOverride || '');
    // eslint-disable-next-line
  }, [lead.id]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const preset = JV_STATUS_PRESETS.find(p => p.value === selection);
      let statusValue = '';
      let colorValue = '';
      if (selection === '__custom') {
        statusValue = customText.trim();
        colorValue = customColor;
      } else if (preset && preset.value) {
        statusValue = preset.value;
        colorValue = preset.color;
      }
      await onUpdate({
        jvStatusOverride: statusValue || null,
        jvStatusColorOverride: colorValue || null,
        jvNotesOverride: notes.trim() || null
      });
      setMsg('✓ Saved. Partner view refreshes within 60s.');
      setTimeout(() => setMsg(''), 2500);
    } finally { setSaving(false); }
  };

  const clearOverride = async () => {
    setSelection(''); setCustomText(''); setNotes('');
    await onUpdate({ jvStatusOverride: null, jvStatusColorOverride: null, jvNotesOverride: null });
    setMsg('✓ Cleared — back to auto.');
    setTimeout(() => setMsg(''), 2000);
  };

  return (
    <Card
      title="JV partner view"
      right={<Pill color="gold">Shown on shared link</Pill>}
      style={{ marginBottom: 16 }}
    >
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.55, marginBottom: 12 }}>
        Override what your JV partner sees for this lead. Leave on <strong>Auto</strong> and the system fills status + notes based on outreach activity (texts sent, replies, blocked numbers, opt-outs).
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
        <Field label="Status">
          <Select value={selection} onChange={e => setSelection(e.target.value)}>
            {JV_STATUS_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </Field>
        {selection === '__custom' && (
          <Field label="Color">
            <Select value={customColor} onChange={e => setCustomColor(e.target.value)}>
              <option value="green">Green — good news</option>
              <option value="gold">Gold — in progress</option>
              <option value="blue">Blue — working it</option>
              <option value="red">Red — dead / issue</option>
              <option value="gray">Gray — neutral</option>
            </Select>
          </Field>
        )}
      </div>

      {selection === '__custom' && (
        <Field label="Custom status text">
          <Input value={customText} onChange={e => setCustomText(e.target.value)} placeholder="e.g. Waiting on title docs" />
        </Field>
      )}

      <Field label="Note shown to partner" hint="Short — one sentence. Leave blank to auto-generate from activity.">
        <TextArea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Seller asked us to call back after 6pm ET Thursday" />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <Btn variant="ghost" onClick={clearOverride} disabled={saving}>Reset to auto</Btn>
          <Btn onClick={save} disabled={saving || (selection === '__custom' && !customText.trim())}>
            {saving ? 'Saving…' : 'Save partner view'}
          </Btn>
        </div>
      </div>
    </Card>
  );
}

// Structured intake for the four-pillars discovery call. Each field
// auto-saves on blur; the whole panel can be marked complete which
// advances the timeline to step 3.
// Blueprint 4 Pillars — ask these verbatim, in this order. Save the answer
// before moving on. Price ALWAYS goes last (silence rule).
const PILLAR_FIELDS = [
  { key: 'motivation', label: 'Motivation', prompt: '"Why are you selling?" Dig past the surface — foreclosure, divorce, inheritance, tired tenants. Deeper pain = more flexible on price.' },
  { key: 'timeline',   label: 'Timeline',   prompt: '"How soon do you need to sell?" Needs 2 weeks = flexible. "As soon as possible" is your opening — speed is your value.' },
  { key: 'condition',  label: 'Condition',  prompt: '"What repairs does it need?" Get a ballpark — roof, HVAC, foundation, cosmetic. Enough to not overbid; buyer verifies on walkthrough.' },
  { key: 'price',      label: 'Price',      prompt: '"What price were you hoping?" Ask LAST. Then stop talking — the seller who speaks first concedes first.' }
];

function SellerCallPanel({ lead, onUpdate, agent }) {
  const sc = lead.sellerCall || {};
  const isDone = !!sc.completedAt;
  const [form, setForm] = useState({
    motivation: sc.motivation || '',
    timeline:   sc.timeline   || '',
    condition:  sc.condition  || '',
    price:      sc.price      || ''
  });
  const [savedKey, setSavedKey] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [busy, setBusy]         = useState(false);

  const savedRef = useRef(form);

  const saveField = async (key, value) => {
    if (savedRef.current[key] === value) return;
    setSaving(true);
    try {
      await onUpdate({
        [`sellerCall.${key}`]: value,
        [`sellerCall.updatedAt`]: serverTimestamp(),
        [`sellerCall.updatedBy`]: { uid: agent?.uid || '', name: agent?.name || '' }
      });
      savedRef.current = { ...savedRef.current, [key]: value };
      setSavedKey(key);
      setTimeout(() => setSavedKey(k => (k === key ? null : k)), 1500);
    } finally {
      setSaving(false);
    }
  };

  const clearField = async (key) => {
    setForm(f => ({ ...f, [key]: '' }));
    await saveField(key, '');
  };

  const markComplete = async () => {
    setBusy(true);
    try {
      // Flush any unsaved edits first.
      for (const f of PILLAR_FIELDS) {
        if (form[f.key] !== savedRef.current[f.key]) {
          await saveField(f.key, form[f.key]);
        }
      }
      await onUpdate({
        'sellerCall.completedAt': serverTimestamp(),
        'sellerCall.completedBy': { uid: agent?.uid || '', name: agent?.name || '' }
      });
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await onUpdate({
        'sellerCall.completedAt': null,
        'sellerCall.completedBy': null
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Four pillars"
      right={isDone
        ? <Pill color="green">Call complete</Pill>
        : <Pill color="gold">In progress</Pill>}
    >
      <div style={{ fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }}>
        Type your notes for each pillar as the call unfolds. Answers save automatically when you move to the next field.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {PILLAR_FIELDS.map(f => (
          <div key={f.key} style={pl.pillarCard}>
            <div style={pl.pillarHead}>
              <div>
                <div style={pl.pillarLabel}>{f.label}</div>
                <div style={pl.pillarPrompt}>{f.prompt}</div>
              </div>
              {savedKey === f.key && <span style={pl.savedTag}>✓ Saved</span>}
            </div>
            <TextArea
              rows={3}
              value={form[f.key]}
              onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}
              onBlur={() => saveField(f.key, form[f.key])}
              placeholder={`Notes about ${f.label.toLowerCase()}…`}
              style={{ fontFamily: "'Inter', sans-serif", fontSize: 14 }}
              disabled={isDone}
            />
            {form[f.key] && !isDone && (
              <button
                type="button"
                onClick={() => clearField(f.key)}
                style={pl.clearBtn}
                aria-label={`Clear ${f.label}`}
              >Clear</button>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: LIGHT }}>
          {saving ? 'Saving…' :
           isDone ? `Completed ${sc.completedAt?.toDate?.()?.toLocaleString?.() || ''}${sc.completedBy?.name ? ` · ${sc.completedBy.name}` : ''}` :
           'Draft — mark complete when the call is done.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isDone
            ? <Btn variant="ghost" onClick={reopen} disabled={busy}>Reopen</Btn>
            : <Btn variant="primary" onClick={markComplete} disabled={busy}>{busy ? 'Marking…' : 'Mark call complete'}</Btn>}
        </div>
      </div>
    </Card>
  );
}

const pl = {
  pillar: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#1C1C1C', lineHeight: 1.55 },
  pillarCard: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' },
  pillarHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  pillarLabel: { fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' },
  pillarPrompt: { fontSize: 12, color: '#6B7280', marginTop: 2, lineHeight: 1.4 },
  savedTag: { fontSize: 11, fontWeight: 600, color: '#166534', background: '#DCFCE7', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  clearBtn: { alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', padding: '2px 0', fontFamily: "'Inter', sans-serif", textDecoration: 'underline' }
};

// ─────── Property Panel ───────
function PropertyPanel({ lead, onUpdate, fullAddr }) {
  const [enriching, setEnriching] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [calling, setCalling] = useState(false);
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const pd = lead.propertyData || {};
  const hasParseSource = !!(lead.parseSource?.text);
  const ownerPhone = (lead.owners || [])[0]?.phone || '';

  const startEdit = () => {
    setForm({
      assetType: lead.assetType === 'land' ? 'land' : 'property',
      address: { ...(lead.address || { line1: '', city: '', state: '', zip: '' }) },
      listingUrl: lead.listingUrl || '',
      owners: (lead.owners || []).length
        ? lead.owners.map(o => ({
            name: o.name || '',
            phone: o.phone || '',
            altPhones: Array.isArray(o.altPhones) ? o.altPhones.filter(Boolean) : [],
            email: o.email || ''
          }))
        : [{ name: '', phone: '', altPhones: [], email: '' }],
      propertyData: {
        askingPrice: pd.askingPrice ?? '',
        beds: pd.beds ?? '',
        baths: pd.baths ?? '',
        sqft: pd.sqft ?? '',
        yearBuilt: pd.yearBuilt ?? '',
        lotSize: pd.lotSize ?? '',
        lotSizeUnit: pd.lotSizeUnit || 'sqft',
        propertyType: pd.propertyType || '',
        county: pd.county || ''
      }
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setForm(null); };

  const saveEdit = async () => {
    if (!form) return;
    setSavingEdit(true);
    try {
      const num = v => (v === '' || v == null ? null : Number(v));
      const cleanOwners = form.owners
        .filter(o => o.name?.trim() || o.phone?.trim() || o.email?.trim() || (o.altPhones || []).some(p => p.trim()))
        .map(o => ({
          name: (o.name || '').trim(),
          phone: (o.phone || '').trim(),
          altPhones: (o.altPhones || []).map(p => (p || '').trim()).filter(Boolean),
          email: (o.email || '').trim()
        }));
      await onUpdate({
        assetType: form.assetType === 'land' ? 'land' : 'property',
        address: {
          line1: (form.address.line1 || '').trim(),
          city: (form.address.city || '').trim(),
          state: (form.address.state || '').trim().toUpperCase().slice(0, 2),
          zip: (form.address.zip || '').trim()
        },
        listingUrl: (form.listingUrl || '').trim(),
        owners: cleanOwners.length ? cleanOwners : (lead.owners || []),
        propertyData: {
          ...(lead.propertyData || {}),
          askingPrice: num(form.propertyData.askingPrice),
          beds: num(form.propertyData.beds),
          baths: num(form.propertyData.baths),
          sqft: num(form.propertyData.sqft),
          yearBuilt: num(form.propertyData.yearBuilt),
          lotSize: num(form.propertyData.lotSize),
          lotSizeUnit: form.propertyData.lotSizeUnit || 'sqft',
          propertyType: (form.propertyData.propertyType || '').trim(),
          county: (form.propertyData.county || '').trim()
        }
      });
      setEditing(false);
      setForm(null);
      setMsg('✓ Saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg(`Save failed: ${e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const setOwner = (i, k, v) => setForm(f => ({
    ...f,
    owners: f.owners.map((o, idx) => idx === i ? { ...o, [k]: v } : o)
  }));
  const addOwner = () => setForm(f => ({ ...f, owners: [...f.owners, { name: '', phone: '', email: '' }] }));
  const removeOwner = (i) => setForm(f => ({ ...f, owners: f.owners.filter((_, idx) => idx !== i) }));
  const setPd = (k, v) => setForm(f => ({ ...f, propertyData: { ...f.propertyData, [k]: v } }));
  const setAddr = (k, v) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));

  const callOwner = async () => {
    if (!ownerPhone) return;
    setCalling(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'initiateCall');
      const res = await fn({ leadId: lead.id, to: ownerPhone });
      setMsg(res.data?.message || 'Calling…');
    } catch (e) {
      const m = e.message || '';
      if (/not linked/i.test(m) || /quoUserId/i.test(m)) {
        setMsg(`Couldn't call: your phone account isn't linked yet. Ask the admin to set your phone user ID.`);
      } else if (/A2P/i.test(m)) {
        setMsg(`Couldn't call: ${m}`);
      } else {
        setMsg(`Couldn't call: ${m}`);
      }
    } finally {
      setCalling(false);
      setTimeout(() => setMsg(''), 6000);
    }
  };

  const enrich = async () => {
    setEnriching(true);
    try {
      const fn = httpsCallable(functions, 'enrichProperty');
      const res = await fn({ leadId: lead.id, address: fullAddr });
      console.log('enrich result', res.data);
    } catch (e) {
      alert(`Enrichment unavailable yet: ${e.message}\n\nThis hooks into the RentCast/ATTOM API. Add the API key in Cloud Functions to enable.`);
    } finally {
      setEnriching(false);
    }
  };

  const reparse = async () => {
    if (!hasParseSource) return;
    setReparsing(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'parseListingText');
      const res = await fn({ url: lead.parseSource.url, text: lead.parseSource.text });
      if (!res.data?.ok) throw new Error(res.data?.message || 'Parse failed.');
      const p = res.data.parsed || {};
      // Merge parsed back into the lead doc (top-level + nested groups).
      await onUpdate({
        address: p.address ? { ...lead.address, ...p.address } : lead.address,
        owners: (p.owners && p.owners.length ? p.owners : lead.owners),
        listingUrl: p.listingUrl || lead.listingUrl,
        propertyData: {
          ...(lead.propertyData || {}),
          ...(p.propertyData || {}),
          askingPrice: p.askingPrice ?? lead.propertyData?.askingPrice ?? null,
          beds:        p.beds        ?? lead.propertyData?.beds        ?? null,
          baths:       p.baths       ?? lead.propertyData?.baths       ?? null,
          sqft:        p.sqft        ?? lead.propertyData?.sqft        ?? null
        },
        listing: { ...(lead.listing || {}), ...(p.listing || {}) },
        financial: { ...(lead.financial || {}), ...(p.financial || {}) },
        saleHistory: Array.isArray(p.saleHistory) ? p.saleHistory : (lead.saleHistory || []),
        taxHistory:  Array.isArray(p.taxHistory)  ? p.taxHistory  : (lead.taxHistory  || []),
        climate: { ...(lead.climate || {}), ...(p.climate || {}) },
        neighborhood: { ...(lead.neighborhood || {}), ...(p.neighborhood || {}) },
        schools: Array.isArray(p.schools) ? p.schools : (lead.schools || [])
      });
      setMsg('✓ Re-parsed and saved.');
    } catch (e) {
      setMsg(`Re-parse failed: ${e.message}`);
    } finally {
      setReparsing(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  return (
    <Card
      title="Property details"
      right={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {editing ? (
            <>
              <Btn variant="ghost" onClick={cancelEdit} disabled={savingEdit}>Cancel</Btn>
              <Btn variant="gold" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</Btn>
            </>
          ) : (
            <>
              {ownerPhone && (
                <Btn variant="gold" onClick={callOwner} disabled={calling} title={`Call ${ownerPhone} via your business number`}>
                  {calling ? 'Calling…' : `📞 Call ${ownerPhone}`}
                </Btn>
              )}
              <Btn variant="ghost" onClick={startEdit}>✎ Edit</Btn>
              {hasParseSource && (
                <Btn variant="ghost" onClick={reparse} disabled={reparsing} title="Re-run AI parse on the saved listing text">
                  {reparsing ? 'Re-parsing…' : '🪄 Re-parse'}
                </Btn>
              )}
              <Btn variant="ghost" onClick={enrich} disabled={enriching}>{enriching ? 'Pulling…' : 'Auto-fill'}</Btn>
            </>
          )}
        </div>
      }
    >
      {editing ? (
        <PropertyEditForm
          form={form}
          setAddr={setAddr}
          setPd={setPd}
          setOwner={setOwner}
          addOwner={addOwner}
          removeOwner={removeOwner}
          onListingUrl={(v) => setForm(f => ({ ...f, listingUrl: v }))}
          onAssetType={(v) => setForm(f => ({ ...f, assetType: v }))}
        />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999,
              color: BURG_DK, background: GOLD_PALE, border: '1px solid rgba(201,168,76,0.5)'
            }}>
              {lead.assetType === 'land' ? '🌳 Land for sale' : '🏠 Property for sale'}
            </span>
          </div>
          <div style={s.kvGrid}>
            <KV label="Address" value={fullAddr
              ? <a href={zillowUrl(fullAddr)} target="_blank" rel="noreferrer" style={{ color: BURG_DK, textDecoration: 'none', borderBottom: `1px dotted ${BURG}` }} title="View on Zillow">{fullAddr}</a>
              : '—'} />
            <KV label="Listing URL" value={lead.listingUrl ? <a href={lead.listingUrl} target="_blank" rel="noreferrer" style={{ color: BURG, textDecoration: 'underline' }}>open ↗</a> : '—'} />
            <KV label="Owner(s)" value={(lead.owners || []).map(o => o.name).filter(Boolean).join(', ') || '—'} />
            <KV label="Phone" value={(lead.owners || [])[0]?.phone || '—'} mono />
            <KV label="Asking price" value={pd.askingPrice ? `$${Number(pd.askingPrice).toLocaleString()}` : '—'} />
            <KV label="Beds / Baths" value={pd.beds && pd.baths ? `${pd.beds} / ${pd.baths}` : '—'} />
            <KV label="Sq Ft" value={pd.sqft ? Number(pd.sqft).toLocaleString() : '—'} />
            <KV label="Year built" value={pd.yearBuilt || '—'} />
            <KV label="Lot size" value={pd.lotSize ? `${Number(pd.lotSize).toLocaleString()} ${pd.lotSizeUnit || 'sqft'}` : '—'} />
            <KV label="Type" value={pd.propertyType || '—'} />
            <KV label="County" value={pd.county || '—'} />
          </div>
          {!pd.sqft && (
            <div style={s.tip}>
              Click <strong>✎ Edit</strong> to fill in the property details, or <strong>Auto-fill</strong> to pull from county records.
            </div>
          )}
        </>
      )}
      {msg && <div style={s.msgBox}>{msg}</div>}
    </Card>
  );
}

// ─────── Multiple-phone editor for one owner ───────
// The seller might have a cell + a landline + a relative's number — we
// support all of them. Primary stays in owner.phone (the one we call /
// text by default); the rest live in owner.altPhones[].
function AltPhonesEditor({ value, onChange }) {
  const phones = Array.isArray(value) ? value : [];

  const setAt = (i, v) => onChange(phones.map((p, idx) => idx === i ? v : p));
  const removeAt = (i) => onChange(phones.filter((_, idx) => idx !== i));
  const add = () => onChange([...phones, '']);

  if (phones.length === 0) {
    return (
      <button type="button" onClick={add} style={altS.addBtn}>+ Add another phone number</button>
    );
  }
  return (
    <>
      {phones.map((p, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 32px', gap: 12, alignItems: 'end' }}>
          <Field label={`Additional phone${phones.length > 1 ? ` ${i + 1}` : ''}`}>
            <Input value={p} onChange={e => setAt(i, e.target.value)} placeholder="(555) 987-6543" inputMode="tel" />
          </Field>
          <button type="button" onClick={() => removeAt(i)} style={s.removeOwnerBtn} title="Remove number">−</button>
        </div>
      ))}
      <button type="button" onClick={add} style={altS.addBtn}>+ Add another phone number</button>
    </>
  );
}

const altS = {
  addBtn: { background: 'transparent', border: 'none', color: BURG, fontSize: 12, padding: '4px 0 8px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};

// ─────── Property edit form ───────
function PropertyEditForm({ form, setAddr, setPd, setOwner, addOwner, removeOwner, onListingUrl, onAssetType }) {
  return (
    <>
      <div style={s.editSection}>Address</div>
      <Field label="Street" required hint="Type to search — Google fills city/state/zip.">
        <AddressAutocomplete
          value={form.address.line1}
          onChange={e => setAddr('line1', e.target.value)}
          onSelect={(parts) => {
            setAddr('line1', parts.line1 || form.address.line1);
            setAddr('city',  parts.city  || form.address.city);
            setAddr('state', parts.state || form.address.state);
            setAddr('zip',   parts.zip   || form.address.zip);
          }}
          placeholder="2847 Thornwood Lane"
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
        <Field label="City"><Input value={form.address.city} onChange={e => setAddr('city', e.target.value)} /></Field>
        <Field label="State"><Input value={form.address.state} onChange={e => setAddr('state', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} /></Field>
        <Field label="Zip"><Input value={form.address.zip} onChange={e => setAddr('zip', e.target.value)} /></Field>
      </div>

      <Field label="Listing URL"><Input type="url" value={form.listingUrl} onChange={e => onListingUrl(e.target.value)} /></Field>

      <div style={s.editSection}>Owners</div>
      {form.owners.map((o, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: 12, alignItems: 'end' }}>
            <Field label={i === 0 ? 'Name' : `Owner ${i + 1}`} required={i === 0}>
              <Input value={o.name} onChange={e => setOwner(i, 'name', e.target.value)} placeholder="Jane Doe" />
            </Field>
            <Field label="Primary phone" required={i === 0}>
              <Input value={o.phone} onChange={e => setOwner(i, 'phone', e.target.value)} placeholder="(555) 123-4567" inputMode="tel" />
            </Field>
            {i > 0 ? (
              <button type="button" onClick={() => removeOwner(i)} style={s.removeOwnerBtn} title="Remove owner">−</button>
            ) : <span />}
          </div>
          <AltPhonesEditor
            value={Array.isArray(o.altPhones) ? o.altPhones : []}
            onChange={(next) => setOwner(i, 'altPhones', next)}
          />
          <Field label="Email"><Input type="email" value={o.email} onChange={e => setOwner(i, 'email', e.target.value)} placeholder="jane@example.com" /></Field>
        </React.Fragment>
      ))}
      <button type="button" onClick={addOwner} style={s.addOwnerBtn}>+ Add another owner</button>

      <div style={s.editSection}>Type</div>
      <Field label="What's for sale?">
        <Select value={form.assetType || 'property'} onChange={e => onAssetType(e.target.value)}>
          <option value="property">🏠 Property (house / structure)</option>
          <option value="land">🌳 Land (vacant land)</option>
        </Select>
      </Field>

      <div style={s.editSection}>Property data</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 12 }}>
        <Field label="Asking price ($)"><Input type="number" value={form.propertyData.askingPrice} onChange={e => setPd('askingPrice', e.target.value)} /></Field>
        <Field label="Beds"><Input type="number" value={form.propertyData.beds} onChange={e => setPd('beds', e.target.value)} /></Field>
        <Field label="Baths"><Input type="number" step="0.5" value={form.propertyData.baths} onChange={e => setPd('baths', e.target.value)} /></Field>
        <Field label="Sq Ft"><Input type="number" value={form.propertyData.sqft} onChange={e => setPd('sqft', e.target.value)} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Year built"><Input type="number" value={form.propertyData.yearBuilt} onChange={e => setPd('yearBuilt', e.target.value)} /></Field>
        <Field label="Lot size"><Input type="number" value={form.propertyData.lotSize} onChange={e => setPd('lotSize', e.target.value)} /></Field>
        <Field label="Lot unit">
          <Select value={form.propertyData.lotSizeUnit || 'sqft'} onChange={e => setPd('lotSizeUnit', e.target.value)}>
            <option value="sqft">sqft</option><option value="acres">acres</option>
          </Select>
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Property type"><Input value={form.propertyData.propertyType} onChange={e => setPd('propertyType', e.target.value)} placeholder="SingleFamily, Condo, …" /></Field>
        <Field label="County"><Input value={form.propertyData.county} onChange={e => setPd('county', e.target.value)} /></Field>
      </div>
    </>
  );
}

const KV = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 11, color: LIGHT, fontWeight: 500, marginBottom: 4, fontFamily: "'Inter', sans-serif" }}>{label}</div>
    <div style={{ fontSize: 14, color: '#111827', fontWeight: 500, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : "'Inter', sans-serif", lineHeight: 1.4 }}>{value}</div>
  </div>
);

// ─────── Comps Panel (RentCast AVM + comparables) ───────
function CompsPanel({ lead, fullAddr, onUpdate, canEdit = false }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualForm, setManualForm] = useState(blankManualComp());
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseMsg, setParseMsg] = useState('');

  const parseAndAddComps = async () => {
    if (!pasteText.trim()) { setParseMsg('Paste some comp text first.'); return; }
    setParsing(true); setParseMsg('');
    try {
      // Client-side Zillow URL extraction — always runs, doesn't depend on
      // the AI cooperating. Any /homedetails/ URL becomes a comp with its
      // address decoded from the slug.
      const urls = extractZillowUrls(pasteText);
      const urlSeeds = urls.map(u => ({
        address: addressFromZillowUrl(u) || '(address from URL)',
        url: u,
        price: null, sqft: null, beds: null, baths: null,
        soldDate: null, yearBuilt: null
      }));

      // Then send the whole paste to the AI to catch prices, sqft, and any
      // non-URL rows. AI enrichment is optional — URL seeds already carry us.
      let aiComps = [];
      try {
        const fn = httpsCallable(functions, 'aiAssist');
        const res = await fn({ task: 'parse-comps', lead: { rawText: pasteText } });
        const text = res.data?.text || '';
        const jsonStr = text.match(/\{[\s\S]*\}/)?.[0];
        const parsed = jsonStr ? JSON.parse(jsonStr) : null;
        aiComps = Array.isArray(parsed?.comps) ? parsed.comps : [];
      } catch (aiErr) {
        // AI failed but URL seeds may still exist — keep going.
        console.warn('parse-comps AI failed:', aiErr.message);
      }

      // Merge — AI comps enrich URL seeds when the URLs match; otherwise
      // they add as new rows.
      const byKey = new Map();
      const keyOf = c => (c.url || c.address || '').toLowerCase().trim();
      [...urlSeeds, ...aiComps].forEach(c => {
        if (!c) return;
        const k = keyOf(c);
        if (!k) return;
        const existing = byKey.get(k) || {};
        byKey.set(k, {
          ...existing,
          ...Object.fromEntries(Object.entries(c).filter(([_, v]) => v != null && v !== ''))
        });
      });
      const merged = Array.from(byKey.values());

      if (!merged.length) {
        setParseMsg('No comps detected. Paste at least one Zillow URL or an address + sold price.');
        return;
      }

      const list = merged;
      // Accept a comp if it has an address AND (a price OR a URL). URL-only
      // rows are added so the agent can drop in the price later without
      // re-pasting the link.
      const newComps = list
        .filter(c => c.address && (c.price || c.url))
        .map(c => ({
          _id: 'manual-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
          formattedAddress: String(c.address).trim(),
          addressLine1: String(c.address).trim(),
          beds: c.beds ?? null,
          baths: c.baths ?? null,
          sqft: c.sqft ?? null,
          yearBuilt: c.yearBuilt ?? null,
          lastSaleDate: c.soldDate ?? null,
          lastSalePrice: c.price ?? null,
          distance: null,
          url: c.url ?? null,
          notes: 'Parsed from paste',
          source: 'manual'
        }));
      if (!newComps.length) {
        setParseMsg('Each comp needs at least an address and a sold price.');
        return;
      }

      // Add all comps + suggest an ARV from the median of every comp on the lead.
      const allPrices = [
        ...((lead.comps || []).map(c => c.lastSalePrice).filter(Boolean)),
        ...((lead.manualComps || []).map(c => c.lastSalePrice).filter(Boolean)),
        ...newComps.map(c => c.lastSalePrice)
      ];
      const suggestedArv = allPrices.length ? Math.round(median(allPrices)) : null;

      const patch = { manualComps: arrayUnion(...newComps) };
      if (suggestedArv) patch['calc.arv'] = suggestedArv;
      await onUpdate(patch);

      setParseMsg(`✓ Added ${newComps.length} comp${newComps.length === 1 ? '' : 's'}${suggestedArv ? ` · ARV set to ${fmtMoney(suggestedArv)}` : ''}.`);
      setPasteText('');
      setTimeout(() => { setShowPaste(false); setParseMsg(''); }, 2200);
    } catch (e) {
      setParseMsg(`Parse failed: ${e.message || 'unknown error'}`);
    } finally {
      setParsing(false);
    }
  };

  const askAi = async () => {
    if (!fullAddr) { setAiMsg('Fill in the address before running the AI estimate.'); return; }
    setAiLoading(true); setAiMsg('');
    try {
      const fn = httpsCallable(functions, 'aiAssist');
      const res = await fn({
        task: 'arv-analysis',
        lead: {
          address: fullAddr,
          owner: (lead.owners || [])[0]?.name || '',
          propertyData: lead.propertyData || {},
          notes: (lead.notes || []).slice(-3).map(n => `${n.type}: ${n.body}`).join(' | ')
        }
      });
      const text = res.data?.text || '';
      let parsed = null;
      try {
        const jsonStr = text.match(/\{[\s\S]*\}/)?.[0];
        parsed = jsonStr ? JSON.parse(jsonStr) : null;
      } catch {}
      await onUpdate({
        aiEstimate: {
          raw: text,
          arvLow: parsed?.arv_low ?? null,
          arvMid: parsed?.arv_mid ?? null,
          arvHigh: parsed?.arv_high ?? null,
          confidence: parsed?.confidence || 'medium',
          reasoning: parsed?.reasoning || '',
          generatedAt: serverTimestamp()
        }
      });
      setAiMsg('✓ AI estimate generated.');
      setTimeout(() => setAiMsg(''), 3000);
    } catch (e) {
      setAiMsg(`AI estimate failed: ${e.message || 'unknown error'}`);
    } finally {
      setAiLoading(false);
    }
  };

  const hiddenKeys = new Set(lead.hiddenCompKeys || []);
  const compKey = (c) => String(c.formattedAddress || c.addressLine1 || '').trim().toLowerCase();
  const rentcastComps = (lead.comps || []).filter(c => !hiddenKeys.has(compKey(c)));
  const manualComps = lead.manualComps || [];
  const comps = [
    ...rentcastComps.map(c => ({ ...c, _source: 'rentcast' })),
    ...manualComps.map(c => ({ ...c, _source: 'manual' }))
  ];

  const hideRentcastComp = async (c) => {
    const key = compKey(c);
    if (!key) return;
    await onUpdate({ hiddenCompKeys: arrayUnion(key) });
  };

  // Inline edit for manual-comp fields. Click a cell → input appears →
  // blur/Enter saves; Escape cancels. Firestore doesn't support array-index
  // updates, so we rewrite manualComps whole with the edited row spliced in.
  const [editingKey, setEditingKey] = useState(null);      // "compId:field"
  const [pendingValue, setPendingValue] = useState('');

  const startEdit = (comp, field) => {
    setEditingKey(`${comp._id}:${field}`);
    setPendingValue(comp[field] == null ? '' : String(comp[field]));
  };
  const cancelEdit = () => { setEditingKey(null); setPendingValue(''); };

  const saveEdit = async (comp, field) => {
    const raw = String(pendingValue).trim();
    let val;
    if (raw === '') val = null;
    else if (field === 'lastSaleDate') val = raw;
    else val = numOrNull(raw);
    const current = lead.manualComps || [];
    const updated = current.map(c => c._id === comp._id ? { ...c, [field]: val } : c);
    await onUpdate({ manualComps: updated });

    // If price changed, re-suggest ARV from the new median.
    if (field === 'lastSalePrice') {
      const prices = [
        ...((lead.comps || []).map(x => x.lastSalePrice).filter(Boolean)),
        ...updated.map(x => x.lastSalePrice).filter(Boolean)
      ];
      if (prices.length) await onUpdate({ 'calc.arv': Math.round(median(prices)) });
    }
    cancelEdit();
  };

  const editableCell = (comp, field, displayValue, formatter) => {
    const canEditThis = canEdit && comp._source === 'manual';
    const key = `${comp._id}:${field}`;
    if (editingKey === key) {
      return (
        <input
          autoFocus
          value={pendingValue}
          onChange={e => setPendingValue(e.target.value)}
          onBlur={() => saveEdit(comp, field)}
          onKeyDown={e => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') cancelEdit();
          }}
          style={{ width: 78, padding: '2px 6px', fontSize: 12, fontFamily: "'Inter', sans-serif", border: `1px solid ${BURG}`, borderRadius: 4, textAlign: 'right', outline: 'none' }}
        />
      );
    }
    const rendered = displayValue == null ? '—' : (formatter ? formatter(displayValue) : displayValue);
    if (!canEditThis) return rendered;
    return (
      <span
        onClick={() => startEdit(comp, field)}
        title="Click to edit"
        style={{ cursor: 'pointer', borderBottom: `1px dotted rgba(107,30,46,0.35)`, padding: '0 2px' }}
      >{rendered}</span>
    );
  };
  const avm = lead.avm || {};
  const lastFetched = lead.compsLastFetchedAt;

  const pull = async () => {
    setLoading(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'pullComps');
      const res = await fn({ leadId: lead.id, address: fullAddr });
      if (!res.data?.ok) {
        setMsg(res.data?.message || 'No comparables found.');
      } else {
        setMsg(`✓ Found ${res.data.count} comparable${res.data.count === 1 ? '' : 's'}.`);
        setTimeout(() => setMsg(''), 3000);
      }
    } catch (e) {
      const m = e.message || '';
      if (/RENTCAST/i.test(m) || /not set/i.test(m)) {
        setMsg('RentCast API key not configured. Add it via `firebase functions:secrets:set RENTCAST_API_KEY` and redeploy.');
      } else {
        setMsg(`Couldn't pull comps: ${m}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // AUTO-PULL DISABLED. RentCast is a paid API (free tier: 50 req/mo).
  // Every lead-open used to fire up to 4 fallback calls — trivial to
  // exhaust the quota. Pulling is now explicit: agent clicks 🔄 RentCast.

  const lastFetchedLabel = lastFetched?.toDate?.()?.toLocaleDateString?.() || '—';

  // ── Manual comp handlers ──
  const setMf = (k, v) => setManualForm(f => ({ ...f, [k]: v }));
  const addManualComp = async () => {
    const entry = {
      _id: 'manual-' + Date.now(),
      formattedAddress: manualForm.address.trim(),
      addressLine1: manualForm.address.trim(),
      beds: numOrNull(manualForm.beds),
      baths: numOrNull(manualForm.baths),
      sqft: numOrNull(manualForm.sqft),
      yearBuilt: numOrNull(manualForm.yearBuilt),
      lastSaleDate: manualForm.saleDate || null,
      lastSalePrice: numOrNull(manualForm.salePrice),
      distance: numOrNull(manualForm.distance),
      url: (manualForm.url || '').trim() || null,
      notes: (manualForm.notes || '').trim() || null,
      source: 'manual'
    };
    if (!entry.formattedAddress || !entry.lastSalePrice) {
      setMsg('Address and sale price are required for a manual comp.');
      return;
    }
    await onUpdate({ manualComps: arrayUnion(entry) });
    setManualForm(blankManualComp());
    setShowAddManual(false);
    setMsg(`✓ Manual comp added.`);
    setTimeout(() => setMsg(''), 3000);
  };
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const requestRemoveManual = (entry) => setPendingRemoval(entry);
  const confirmRemoveManual = async () => {
    if (!pendingRemoval) return;
    await onUpdate({ manualComps: arrayRemove(pendingRemoval) });
    setPendingRemoval(null);
  };

  return (
    <Card
      title="Comparable sales"
      right={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: LIGHT, fontStyle: 'italic' }}>
            {loading ? 'Pulling…' : lastFetched ? `Updated ${lastFetchedLabel}` : 'Not pulled yet'}
          </span>
          <Btn variant="ghost" onClick={pull} disabled={loading} title="RentCast — costs 1–4 API calls">{loading ? '…' : '🔄 RentCast'}</Btn>
          <Btn variant="ghost" onClick={askAi} disabled={aiLoading} title="Ask ChatGPT to estimate ARV from the property details.">{aiLoading ? '…' : '🤖 AI Estimate'}</Btn>
          {canEdit && <Btn variant="ghost" onClick={() => setShowPaste(true)} title="Paste Zillow comp text and let AI parse them">📋 Paste comps</Btn>}
          {canEdit && <Btn variant="gold" onClick={() => setShowAddManual(s => !s)}>{showAddManual ? 'Cancel' : '+ Add manual'}</Btn>}
        </div>
      }
    >
      {/* AVM headline */}
      {avm.price ? (
        <div style={cs.avmRow}>
          <div>
            <div style={cs.avmLabel}>Estimated value (AVM)</div>
            <div style={cs.avmPrice}>{fmtMoney(avm.price)}</div>
            {avm.priceRangeLow && avm.priceRangeHigh && (
              <div style={cs.avmRange}>Range: {fmtMoney(avm.priceRangeLow)} – {fmtMoney(avm.priceRangeHigh)}</div>
            )}
          </div>
          {comps.length > 0 && (
            <div style={cs.avmRight}>
              <div style={cs.avmLabel}>Median comp</div>
              <div style={cs.avmMedian}>{fmtMoney(median(comps.map(c => c.lastSalePrice).filter(Boolean)))}</div>
              <div style={cs.avmRange}>{comps.length} comp{comps.length === 1 ? '' : 's'} · {comps.filter(c => typeof c.distance === 'number').length > 0 ? `${(median(comps.map(c => c.distance).filter(d => typeof d === 'number')) || 0).toFixed(2)} mi avg` : ''}</div>
            </div>
          )}
        </div>
      ) : !loading && (
        <div style={{ ...s.tip, marginBottom: 12 }}>
          {fullAddr ? 'No AVM yet. Click RentCast to pull comps, or AI Estimate for a ChatGPT valuation.' : 'Add the city, state, and zip to enable comp lookup.'}
        </div>
      )}

      {comps.length > 0 && (
        <div style={{ ...s.tip, marginBottom: 12 }}>
          ⚠️ RentCast returns <strong>all recent sales</strong>, renovated and distressed. Click each address to open Zillow — an accurate ARV only uses <strong>renovated / retail</strong> comps. Delete any comp that was sold as-is, cash-only, or a fixer.
        </div>
      )}

      {/* AI ARV estimate — always visible when generated so agent can cross-reference RentCast. */}
      {lead.aiEstimate && (
        <div style={cs.aiCard}>
          <div style={cs.aiHead}>
            <div>
              <div style={cs.aiEyebrow}>ChatGPT ARV estimate</div>
              <div style={cs.aiTitle}>
                {lead.aiEstimate.arvMid
                  ? fmtMoney(lead.aiEstimate.arvMid)
                  : (lead.aiEstimate.arvLow && lead.aiEstimate.arvHigh
                      ? `${fmtMoney(lead.aiEstimate.arvLow)} – ${fmtMoney(lead.aiEstimate.arvHigh)}`
                      : 'See notes')}
                {lead.aiEstimate.confidence && (
                  <span style={{ ...cs.aiConfPill, ...(cs.aiConfBy[lead.aiEstimate.confidence] || {}) }}>
                    {lead.aiEstimate.confidence} confidence
                  </span>
                )}
              </div>
              {lead.aiEstimate.arvLow && lead.aiEstimate.arvHigh && (
                <div style={cs.aiRange}>Range: {fmtMoney(lead.aiEstimate.arvLow)} – {fmtMoney(lead.aiEstimate.arvHigh)}</div>
              )}
            </div>
            {lead.aiEstimate.arvMid && (
              <Btn variant="ghost" onClick={() => onUpdate({ 'calc.arv': lead.aiEstimate.arvMid })}>
                Use as ARV
              </Btn>
            )}
          </div>
          {lead.aiEstimate.reasoning && (
            <div style={cs.aiReasoning}>{lead.aiEstimate.reasoning}</div>
          )}
        </div>
      )}

      {aiMsg && (
        <div style={{ fontSize: 12, color: /failed/i.test(aiMsg) ? '#B91C1C' : '#166534', marginBottom: 12 }}>{aiMsg}</div>
      )}

      {/* Bulk paste modal — AI parses raw Zillow text into structured comps */}
      {showPaste && (
        <div style={mod.backdrop} onClick={() => !parsing && setShowPaste(false)}>
          <div style={{ ...mod.panel, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <header style={mod.head}>
              <div style={mod.title}>📋 Paste comps</div>
              <button style={mod.x} onClick={() => !parsing && setShowPaste(false)} aria-label="Close">×</button>
            </header>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 13, color: MID, lineHeight: 1.55, marginBottom: 12 }}>
                Paste any of these — mix and match:
                <ul style={{ margin: '6px 0 0', paddingLeft: 20, lineHeight: 1.5 }}>
                  <li><strong>Zillow URLs</strong> (one per line) — address auto-extracts; fill in price later.</li>
                  <li><strong>URL + price/sqft on the same line</strong> — fully parsed.</li>
                  <li><strong>Plain text rows</strong> — address · price · sqft · beds · baths.</li>
                </ul>
                Skip active listings (For sale / Pending / For rent). ChatGPT parses and sets ARV from the median.
              </div>
              <TextArea
                rows={10}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                placeholder={'https://www.zillow.com/homedetails/456-Oak-Ave-Atlanta-GA-30303/12345_zpid/  $245,000  1,650 sqft  3 bd  2 ba\nhttps://www.zillow.com/homedetails/789-Pine-St-Atlanta-GA-30303/67890_zpid/\n123 Main St, Atlanta GA 30303 · Sold $232,000 · 1,580 sqft · 3 bd · 2 ba · sold 3/12/2026'}
                style={{ width: '100%', fontFamily: "'Inter', sans-serif", fontSize: 13 }}
                disabled={parsing}
              />
              {parseMsg && (
                <div style={{ fontSize: 12, marginTop: 10, color: /failed|need|no sold/i.test(parseMsg) ? '#B91C1C' : '#166534' }}>{parseMsg}</div>
              )}
            </div>
            <footer style={mod.footer}>
              <Btn variant="ghost" onClick={() => setShowPaste(false)} disabled={parsing}>Cancel</Btn>
              <Btn variant="primary" onClick={parseAndAddComps} disabled={parsing || !pasteText.trim()}>
                {parsing ? 'Parsing…' : 'Parse & add'}
              </Btn>
            </footer>
          </div>
        </div>
      )}

      {/* Manual comp form */}
      {showAddManual && (
        <div style={cs.manualForm}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: BURG, marginBottom: 10 }}>
            Add a comp manually
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10 }}>
            <Field label="Address" required><Input value={manualForm.address} onChange={e => setMf('address', e.target.value)} placeholder="124 Lorena St, Jacksonville, FL" /></Field>
            <Field label="Beds"><Input type="number" value={manualForm.beds} onChange={e => setMf('beds', e.target.value)} /></Field>
            <Field label="Baths"><Input type="number" step="0.5" value={manualForm.baths} onChange={e => setMf('baths', e.target.value)} /></Field>
            <Field label="Sqft"><Input type="number" value={manualForm.sqft} onChange={e => setMf('sqft', e.target.value)} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <Field label="Year built"><Input type="number" value={manualForm.yearBuilt} onChange={e => setMf('yearBuilt', e.target.value)} placeholder="1955" /></Field>
            <Field label="Sale date"><Input type="date" value={manualForm.saleDate} onChange={e => setMf('saleDate', e.target.value)} /></Field>
            <Field label="Sale price ($)" required><Input type="number" value={manualForm.salePrice} onChange={e => setMf('salePrice', e.target.value)} placeholder="195000" /></Field>
            <Field label="Distance (mi)"><Input type="number" step="0.1" value={manualForm.distance} onChange={e => setMf('distance', e.target.value)} placeholder="0.4" /></Field>
          </div>
          <Field label="Listing URL (Zillow / Redfin)"><Input value={manualForm.url} onChange={e => setMf('url', e.target.value)} placeholder="https://www.zillow.com/homedetails/..." /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
            <Btn variant="ghost" onClick={() => { setShowAddManual(false); setManualForm(blankManualComp()); }}>Cancel</Btn>
            <Btn variant="gold" onClick={addManualComp}>+ Add comp</Btn>
          </div>
        </div>
      )}

      {/* Comps table */}
      {comps.length > 0 ? (
        <table style={cs.table}>
          <thead>
            <tr>
              <th style={cs.th}>Address</th>
              <th style={cs.thNum}>Beds</th>
              <th style={cs.thNum}>Baths</th>
              <th style={cs.thNum}>Sqft</th>
              <th style={cs.thNum}>Year</th>
              <th style={cs.thNum}>Sold</th>
              <th style={cs.thNum}>Price</th>
              <th style={cs.thNum}>Distance</th>
              <th style={cs.thNum}>Source</th>
              {canEdit && <th style={cs.thNum}></th>}
            </tr>
          </thead>
          <tbody>
            {comps.map((c, i) => (
              <tr key={c._id || i} style={cs.tr}>
                <td style={cs.td}>
                  <div style={{ fontWeight: 500 }}>
                    {(() => {
                      const addr = c.formattedAddress || c.addressLine1 || '';
                      const href = c.url || zillowUrl(addr);
                      return href
                        ? <a href={href} target="_blank" rel="noreferrer" style={{ color: BURG_DK, textDecoration: 'none', borderBottom: `1px dotted ${BURG}` }} title={c.url ? 'Open source link' : 'View on Zillow'}>{addr}</a>
                        : addr;
                    })()}
                  </div>
                  {c.propertyType && <div style={{ fontSize: 10, color: LIGHT }}>{c.propertyType}</div>}
                </td>
                <td style={cs.tdNum}>{editableCell(c, 'beds', c.beds)}</td>
                <td style={cs.tdNum}>{editableCell(c, 'baths', c.baths)}</td>
                <td style={cs.tdNum}>{editableCell(c, 'sqft', c.sqft, v => v.toLocaleString())}</td>
                <td style={cs.tdNum}>{editableCell(c, 'yearBuilt', c.yearBuilt)}</td>
                <td style={cs.tdNum}>{editableCell(c, 'lastSaleDate', c.lastSaleDate)}</td>
                <td style={{ ...cs.tdNum, fontWeight: 500, color: BURG_DK }}>{editableCell(c, 'lastSalePrice', c.lastSalePrice, v => fmtMoney(v))}</td>
                <td style={cs.tdNum}>{typeof c.distance === 'number' ? `${c.distance.toFixed(2)} mi` : '—'}</td>
                <td style={cs.tdNum}>
                  {c._source === 'manual'
                    ? <Pill color="gold">Manual</Pill>
                    : typeof c.correlation === 'number'
                      ? <span style={cs.matchPill(c.correlation)}>{Math.round(c.correlation * 100)}%</span>
                      : <Pill color="gray">RentCast</Pill>}
                </td>
                {canEdit && (
                  <td style={cs.tdNum}>
                    {c._source === 'manual'
                      ? <button onClick={() => requestRemoveManual(c)} title="Remove this manual comp" style={cs.removeBtn}>×</button>
                      : <button onClick={() => hideRentcastComp(c)} title="Hide this comp (e.g. distressed sale). Won't come back on re-pull." style={cs.removeBtn}>×</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: LIGHT, fontSize: 13 }}>
          Pulling comparables from county records and recent sales…
        </div>
      ) : (
        <div style={{ ...s.tip }}>
          {fullAddr
            ? 'No comps yet. Click 🔄 RentCast to pull, or "+ Add manual" to enter them yourself from Zillow Sold listings.'
            : 'Add the city, state, and zip to enable comp lookup.'}
        </div>
      )}

      {msg && <div style={{ ...s.msgBox, marginTop: 10 }}>{msg}</div>}

      {pendingRemoval && (
        <ConfirmModal
          title="Remove this comp?"
          body={`Take "${pendingRemoval.formattedAddress || pendingRemoval.addressLine1}" off the comps list. You can always add it back manually.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          onCancel={() => setPendingRemoval(null)}
          onConfirm={confirmRemoveManual}
        />
      )}
    </Card>
  );
}

// Generic burgundy/gold confirmation modal — replaces window.confirm.
function ConfirmModal({ title, body, confirmLabel = 'Confirm', confirmVariant = 'primary', onConfirm, onCancel }) {
  return (
    <div style={mod.backdrop} onClick={onCancel}>
      <div style={mod.panel} onClick={e => e.stopPropagation()}>
        <div style={mod.head}>
          <div style={mod.title}>{title}</div>
          <button onClick={onCancel} style={mod.x} aria-label="Close">×</button>
        </div>
        <div style={{ padding: 22, fontSize: 13, color: MID, lineHeight: 1.65 }}>{body}</div>
        <div style={mod.footer}>
          <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
          <Btn variant={confirmVariant} onClick={onConfirm}>{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}

function blankManualComp() {
  return { address: '', beds: '', baths: '', sqft: '', yearBuilt: '', saleDate: '', salePrice: '', distance: '', url: '', notes: '' };
}
function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function median(arr) {
  if (!arr || arr.length === 0) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

const cs = {
  avmRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 6, marginBottom: 14 },
  avmRight: { textAlign: 'right' },
  avmLabel: { fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: LIGHT, marginBottom: 4, fontWeight: 500 },
  avmPrice: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: BURG_DK, lineHeight: 1 },
  avmMedian: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: BURG, lineHeight: 1 },
  avmRange: { fontSize: 11, color: MID, marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  th: { textAlign: 'left', padding: '8px 8px', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}`, fontWeight: 500 },
  thNum: { textAlign: 'right', padding: '8px 8px', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}`, fontWeight: 500 },
  tr: { },
  td: { padding: '10px 8px', borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', verticalAlign: 'top' },
  tdNum: { padding: '10px 8px', borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  matchPill: (corr) => ({
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    background: corr >= 0.85 ? '#E7F5EE' : corr >= 0.7 ? GOLD_PALE : '#F4F4F5',
    color: corr >= 0.85 ? '#0F6B3F' : corr >= 0.7 ? BURG_DK : '#52525B',
    border: '1px solid ' + (corr >= 0.85 ? 'rgba(15,107,63,0.25)' : corr >= 0.7 ? 'rgba(201,168,76,0.4)' : '#E4E4E7')
  }),
  manualForm: { background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 8, padding: '14px 16px', marginBottom: 16 },
  removeBtn: { background: 'transparent', border: '1px solid #FCA5A5', color: '#991B1B', width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 },
  aiCard: { background: '#F9FAFB', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 },
  aiHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  aiEyebrow: { fontSize: 11, color: '#7A5D1A', fontWeight: 600, marginBottom: 4 },
  aiTitle: { fontFamily: "'Manrope', sans-serif", fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  aiConfPill: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, textTransform: 'lowercase', letterSpacing: 0 },
  aiConfBy: {
    low:    { background: '#FEE2E2', color: '#991B1B' },
    medium: { background: GOLD_PALE, color: '#7A5D1A' },
    high:   { background: '#DCFCE7', color: '#166534' }
  },
  aiRange: { fontSize: 13, color: MID, marginTop: 4 },
  aiReasoning: { fontSize: 13, color: MID, lineHeight: 1.55, marginTop: 10, fontFamily: "'Inter', sans-serif" }
};

// ─────── ARV Panel ───────
// Shows the auto-suggested ARV (avg of comps) and lets the agent override.
// Saves to lead.calc.arv. The MAO panel reads from here.
function ArvPanel({ lead, onUpdate, autoArv }) {
  const initial = lead.calc || {};
  const initialArv = initial.arv != null && initial.arv !== '' ? initial.arv : (autoArv ?? lead.avm?.price ?? '');
  const [arv, setArv] = useState(initialArv);
  const [savingState, setSavingState] = useState('idle');

  // If autoArv changes (new comps added) and the agent hasn't overridden,
  // re-suggest. We treat "matches autoArv or matches avm" as not-overridden.
  useEffect(() => {
    const num = Number(arv);
    if (!num && autoArv) setArv(autoArv);
  // eslint-disable-next-line
  }, [autoArv]);

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setSavingState('saving');
    const h = setTimeout(async () => {
      try {
        await onUpdate({
          calc: { ...(lead.calc || {}), arv: Number(arv) || 0, savedAt: Timestamp.now() }
        });
        setSavingState('saved');
        setTimeout(() => setSavingState('idle'), 1500);
      } catch { setSavingState('idle'); }
    }, 800);
    return () => clearTimeout(h);
  // eslint-disable-next-line
  }, [arv]);

  const compCount = (lead.comps || []).length + (lead.manualComps || []).length;
  const savedLabel = savingState === 'saving' ? 'Saving…' : savingState === 'saved' ? '✓ Saved' : '';

  return (
    <Card
      title="After-Repair Value (ARV)"
      right={<span style={{ fontSize: 11, color: savingState === 'saved' ? '#0F6B3F' : LIGHT, fontStyle: 'italic' }}>{savedLabel}</span>}
    >
      {autoArv ? (
        <div style={s.arvSuggestion}>
          <div>
            <div style={s.arvSuggestionLabel}>Suggested ARV — average of your top {Math.min(3, compCount)} comp{compCount === 1 ? '' : 's'}</div>
            <div style={s.arvSuggestionValue}>{fmtMoney(autoArv)}</div>
          </div>
          {Number(arv) !== autoArv && (
            <button type="button" onClick={() => setArv(autoArv)} style={s.useAvmBtn}>Use suggested</button>
          )}
        </div>
      ) : (
        <div style={s.tip}>Add at least one comp in the previous step to get an ARV suggestion.</div>
      )}

      <Field label="ARV ($) — adjust if your gut says different">
        <Input type="number" value={arv} onChange={e => setArv(e.target.value)} placeholder={autoArv ? String(autoArv) : '197000'} style={{ fontSize: 18, fontWeight: 600, color: BURG_DK }} />
      </Field>

      {lead.avm?.price && (
        <div style={{ fontSize: 11, color: MID, marginTop: 6 }}>
          RentCast AVM: <strong>{fmtMoney(lead.avm.price)}</strong>{' '}
          <button type="button" onClick={() => setArv(lead.avm.price)} style={s.useAvmBtn}>Use AVM</button>
        </div>
      )}
    </Card>
  );
}

// ─────── MAO Panel ───────
// Reads ARV from lead.calc.arv (set by ArvPanel) and computes MAO with
// repair tier + assignment fee + location %. Auto-saves to lead.calc.
function MaoPanel({ lead, onUpdate }) {
  const initial = lead.calc || {};
  const arv = initial.arv ?? lead.avm?.price ?? 0;
  const [novationListPrice, setNovationListPrice] = useState(initial.novationListPrice ?? '');
  const [sqft, setSqft] = useState(initial.sqft ?? lead.propertyData?.sqft ?? '');
  const [tier, setTier] = useState(initial.tier || 'moderate');
  const [repairPerSqft, setRepairPerSqft] = useState(initial.repairPerSqft ?? REPAIR_TIERS.moderate.rate);
  const [repairOverride, setRepairOverride] = useState(initial.repairOverride ?? '');
  const [locationPct, setLocationPct] = useState(initial.locationPct ?? DEFAULT_LOCATION_PCT);
  const [assignmentFee, setAssignmentFee] = useState(initial.assignmentFee ?? 15000);
  const [savingState, setSavingState] = useState('idle');

  const setTierAndRate = (k) => { setTier(k); setRepairPerSqft(REPAIR_TIERS[k].rate); };

  // Effective repair cost: if user typed an override $ amount, use that;
  // otherwise sqft × rate from the tier.
  const repairsByFormula = (Number(sqft) || 0) * (Number(repairPerSqft) || 0);
  const effectiveRepairs = (repairOverride !== '' && repairOverride != null) ? Number(repairOverride) : repairsByFormula;

  const calc = useMemo(() => runWholesaleCalc({
    arv: Number(arv) || 0,
    novationListPrice: Number(novationListPrice) || 0,
    sqft: Number(sqft) || 0,
    repairPerSqft: (repairOverride !== '' && repairOverride != null)
      ? (Number(repairOverride) / Math.max(1, Number(sqft) || 1))
      : Number(repairPerSqft) || 0,
    locationPct: Number(locationPct) || 0,
    assignmentFee: Number(assignmentFee) || 0,
    activePlusPending: 0,
    activeOnly: 0
  }), [arv, novationListPrice, sqft, repairPerSqft, repairOverride, locationPct, assignmentFee]);

  const startingOffer = (Number(calc.mao) || 0) - 12500; // mid of Ishoun's "$10–15K below"

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setSavingState('saving');
    const h = setTimeout(async () => {
      try {
        await onUpdate({
          calc: {
            ...(lead.calc || {}),
            arv: Number(arv) || 0,
            novationListPrice: Number(novationListPrice) || 0,
            sqft: Number(sqft) || 0,
            tier, repairPerSqft: Number(repairPerSqft) || 0,
            repairOverride: repairOverride === '' ? null : Number(repairOverride),
            locationPct: Number(locationPct) || 0,
            assignmentFee: Number(assignmentFee) || 0,
            results: calc,
            savedAt: Timestamp.now()
          }
        });
        setSavingState('saved');
        setTimeout(() => setSavingState('idle'), 1500);
      } catch { setSavingState('idle'); }
    }, 900);
    return () => clearTimeout(h);
  // eslint-disable-next-line
  }, [novationListPrice, sqft, tier, repairPerSqft, repairOverride, locationPct, assignmentFee]);

  const savedLabel = savingState === 'saving' ? 'Saving…' : savingState === 'saved' ? '✓ Saved' : '';

  return (
    <Card
      title="Max Allowable Offer (MAO)"
      right={<span style={{ fontSize: 11, color: savingState === 'saved' ? '#0F6B3F' : LIGHT, fontStyle: 'italic' }}>{savedLabel}</span>}
    >
      <div style={s.formulaPreview}>
        <span style={s.formulaTerm}>{fmtMoney(arv)}</span>
        <span style={s.formulaOp}>×</span>
        <span style={s.formulaTerm}>{Number(locationPct)}%</span>
        <span style={s.formulaOp}>−</span>
        <span style={s.formulaTerm}>{fmtMoney(effectiveRepairs)}</span>
        <span style={s.formulaOp}>−</span>
        <span style={s.formulaTerm}>{fmtMoney(Number(assignmentFee))}</span>
        <span style={s.formulaOp}>=</span>
        <span style={s.formulaResult}>{fmtMoney(calc.mao)}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
        <Field label="Square feet"><Input type="number" value={sqft} onChange={e => setSqft(e.target.value)} placeholder="1100" /></Field>
        <Field label="Repair tier" hint="Sets a default $/sqft rate">
          <Select value={tier} onChange={e => setTierAndRate(e.target.value)}>
            {Object.entries(REPAIR_TIERS).map(([k, v]) => (
              <option key={k} value={k}>{v.label} (${v.rate}/sqft)</option>
            ))}
          </Select>
        </Field>
        <Field label="Repair per sqft ($)"><Input type="number" value={repairPerSqft} onChange={e => setRepairPerSqft(e.target.value)} /></Field>
        <Field label={`Repair override ($) — total`} hint="Replace formula with a flat dollar figure">
          <Input type="number" value={repairOverride} onChange={e => setRepairOverride(e.target.value)} placeholder={String(Math.round(repairsByFormula))} />
        </Field>
        <Field label="Location % (default 70)"><Input type="number" value={locationPct} onChange={e => setLocationPct(e.target.value)} /></Field>
        <Field label="Assignment fee ($)"><Input type="number" value={assignmentFee} onChange={e => setAssignmentFee(e.target.value)} placeholder="20000" /></Field>
        <Field label="Novation list price ($) — optional"><Input type="number" value={novationListPrice} onChange={e => setNovationListPrice(e.target.value)} placeholder="175000" /></Field>
      </div>

      <div style={s.calcResults}>
        <ResultRow label="Repair Cost"     value={fmtMoney(effectiveRepairs)} />
        <ResultRow label="MAO (max offer)" value={fmtMoney(calc.mao)} highlight />
        <ResultRow label="Suggested starting offer" value={fmtMoney(startingOffer)} highlight tag={<Pill color="gold">−$12.5K</Pill>} />
        {Number(novationListPrice) > 0 && (
          <ResultRow label="Novation max"  value={fmtMoney(calc.novation)} />
        )}
      </div>

      {/* Per-deal commission split override. Default sits on the agent
          profile (admin sets it). Override here when this specific deal
          warrants a different split. */}
      <div style={s.commissionRow}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 11, color: LIGHT, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Commission split for this deal</div>
          <div style={{ fontSize: 12, color: MID, lineHeight: 1.5 }}>
            Override the default {Math.round(((lead.commissionOverrideToHomeIQa) ?? 0.25) * 100)}% / {100 - Math.round(((lead.commissionOverrideToHomeIQa) ?? 0.25) * 100)}% split if this deal calls for a different split.
          </div>
        </div>
        <CommissionOverrideInput
          value={typeof lead.commissionOverrideToHomeIQa === 'number' ? Math.round(lead.commissionOverrideToHomeIQa * 100) : null}
          onSave={async (pct) => onUpdate({
            commissionOverrideToHomeIQa: pct == null ? null : Math.max(0, Math.min(100, Number(pct))) / 100
          })}
        />
      </div>

      <div style={s.formulaNote}>
        ARV × 70% − Repairs − Assignment Fee = MAO. Never offer the seller MAO directly — start $10K–$15K below.
      </div>
    </Card>
  );
}

// Inline editor for the per-deal commission override. Shows "Use default"
// when null; click to set a custom percent. Returns null on clear so the
// agent default takes over.
function CommissionOverrideInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);

  if (!editing) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button onClick={() => setEditing(true)} style={cmS.pill}>
          {value == null ? 'Use default' : `${value}% to HomeIQa`} ✎
        </button>
      </div>
    );
  }
  const save = async (newVal) => {
    setEditing(false);
    await onSave(newVal === '' ? null : Number(newVal));
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="number"
        autoFocus
        min="0"
        max="100"
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={() => save(v)}
        onKeyDown={e => { if (e.key === 'Enter') save(v); if (e.key === 'Escape') { setEditing(false); setV(value ?? ''); } }}
        style={cmS.input}
        placeholder="25"
      />
      <span style={{ fontSize: 11, color: MID }}>%</span>
      <button onClick={() => save('')} style={cmS.clearBtn} title="Clear override">×</button>
    </div>
  );
}

const cmS = {
  pill: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  input: { width: 50, padding: '5px 6px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 12, fontFamily: "'DM Sans', sans-serif", textAlign: 'right' },
  clearBtn: { background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, width: 22, height: 22, fontSize: 12, color: LIGHT, cursor: 'pointer', padding: 0 }
};

const ResultRow = ({ label, value, highlight, tag }) => (
  <div style={{ ...s.resRow, ...(highlight ? s.resRowHi : {}) }}>
    <div style={s.resLabel}>{label}</div>
    <div style={s.resValue}>{value}</div>
    {tag && <div>{tag}</div>}
  </div>
);

// ─────── Activity Log (notes + SMS unified, scrollable) ───────
// All client-facing activity in one chronological feed: agent notes,
// outbound SMS, inbound replies, and system events (lead closed, etc.).
// Each entry has a clear timestamp and a colored type pill so the eye
// can scan quickly. The list is capped at 480px tall with scroll so the
// page never blows up vertically.
// Render a note body with light markdown-style bold. Splits on **…** and
// wraps the inner text in <strong>. Keeps newlines (parent uses pre-wrap).
function renderNoteBody(body) {
  if (!body) return null;
  const parts = String(body).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function NotesPanel({ lead, onUpdate, agent, defaultType }) {
  const [type, setType] = useState(defaultType || 'Initial Call');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const note = {
        type, body: body.trim(),
        createdAt: Timestamp.now(),
        author: { uid: agent.uid, name: agent.name || agent.email }
      };
      await onUpdate({ notes: arrayUnion(note) });
      setBody('');
    } finally {
      setSaving(false);
    }
  };

  // Merge notes + SMS into one feed sorted newest-first. Each entry gets
  // a normalized shape so the renderer doesn't have to branch.
  const feed = useMemo(() => {
    const items = [];
    (lead.notes || []).forEach((n, i) => {
      items.push({
        kind: 'note',
        key: `note-${i}-${n.createdAt?.toMillis?.() || i}`,
        ts: n.createdAt?.toMillis?.() || 0,
        when: n.createdAt?.toDate?.() || null,
        type: n.type,
        body: n.body,
        author: n.author?.name || 'Agent'
      });
    });
    (lead.smsThread || []).forEach((m, i) => {
      items.push({
        kind: m.direction === 'in' ? 'sms_in' : 'sms_out',
        key: `sms-${i}-${m.sentAt?.toMillis?.() || i}`,
        ts: m.sentAt?.toMillis?.() || 0,
        when: m.sentAt?.toDate?.() || null,
        type: m.direction === 'in' ? 'SMS Received' : 'SMS Sent',
        body: m.body || '',
        author: m.direction === 'out' ? (m.agentName || 'Agent') : (lead.owners?.[0]?.name || 'Seller')
      });
    });
    items.sort((a, b) => b.ts - a.ts);
    return items;
  }, [lead.notes, lead.smsThread, lead.owners]);

  return (
    <Card title="Notes & Call Log" right={feed.length > 0 && <span style={nl.count}>{feed.length} entr{feed.length === 1 ? 'y' : 'ies'}</span>}>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'start' }}>
        <Select value={type} onChange={e => setType(e.target.value)}>
          {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
        </Select>
        <TextArea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={`Quick note for "${type}"…`}
          rows={2}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') add(); }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 11, color: LIGHT }}>⌘/Ctrl + Enter to save</span>
        <Btn onClick={add} disabled={saving || !body.trim()}>{saving ? 'Saving…' : 'Add note'}</Btn>
      </div>

      {feed.length > 0 ? (
        <ul style={nl.list}>
          {feed.map(entry => {
            const isSmsIn = entry.kind === 'sms_in';
            const isSmsOut = entry.kind === 'sms_out';
            const color = isSmsIn ? 'green' : isSmsOut ? 'gold' : noteColor(entry.type);
            return (
              <li key={entry.key} style={{ ...nl.entry, ...(isSmsIn ? nl.entryIn : isSmsOut ? nl.entryOut : {}) }}>
                <div style={nl.head}>
                  <Pill color={color}>{entry.type}</Pill>
                  <span style={nl.when}>{fmtFull(entry.when)}</span>
                </div>
                <div style={nl.body}>{renderNoteBody(entry.body)}</div>
                <div style={nl.who}>— {entry.author}</div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div style={{ fontSize: 12, color: LIGHT, padding: '14px 0 4px', textAlign: 'center' }}>
          No notes or messages yet.
        </div>
      )}
    </Card>
  );
}

function fmtFull(d) {
  if (!d) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const sameYest = d.toDateString() === yest.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today · ${time}`;
  if (sameYest) return `Yesterday · ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' })} · ${time}`;
}

const nl = {
  count: { fontSize: 10, color: LIGHT, fontStyle: 'italic', letterSpacing: '0.04em' },
  list: { listStyle: 'none', padding: 0, marginTop: 14, maxHeight: 700, overflowY: 'auto', borderTop: `1px solid ${BORDER}` },
  entry: { padding: '12px 4px', borderBottom: `1px solid ${BORDER}` },
  entryIn: { background: 'rgba(15, 107, 63, 0.04)', paddingLeft: 12, paddingRight: 12, borderLeft: '3px solid #0F6B3F', borderRadius: '0 6px 6px 0' },
  entryOut: { background: 'rgba(201, 168, 76, 0.06)', paddingLeft: 12, paddingRight: 12, borderLeft: `3px solid ${GOLD}`, borderRadius: '0 6px 6px 0' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  when: { fontSize: 10, color: LIGHT, fontVariantNumeric: 'tabular-nums' },
  body: { fontSize: 13, color: '#1C1C1C', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  who: { fontSize: 10, color: LIGHT, marginTop: 4, fontStyle: 'italic' }
};

const noteColor = (t) => ({
  'Initial Call': 'gold',
  'Follow Up': 'burg',
  'Voicemail': 'gray',
  'Renegotiation': 'burg',
  'Objection': 'red',
  'SMS': 'green',
  'Email': 'green',
  'Other': 'gray'
})[t] || 'gold';

// ─────── Contracts (e-sign) ───────
function ContractsPanel({ lead, onUpdate, agentUid, fullAddr }) {
  const [type, setType] = useState('psa');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [closingDate, setClosingDate] = useState('');
  const [acceptanceDeadline, setAcceptanceDeadline] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [aifName, setAifName] = useState('');
  const [sellerEmail, setSellerEmail] = useState(lead.owners?.[0]?.email || '');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [bizProfile, setBizProfile] = useState(null);

  // Load company-wide contract defaults once. These auto-fill the buyer/AIF
  // fields so every agent's contracts go out under the same LLC name.
  useEffect(() => {
    (async () => {
      try {
        const { getDoc, doc: docRef } = await import('firebase/firestore');
        const snap = await getDoc(docRef(db, 'settings', 'company'));
        if (snap.exists()) setBizProfile(snap.data());
      } catch {}
    })();
  }, []);

  // When contract type changes, pre-fill the buyer/AIF inputs from defaults
  // (only if the agent hasn't typed something custom).
  useEffect(() => {
    if (!bizProfile) return;
    if (!buyerName) setBuyerName(bizProfile.defaultBuyerName || '');
    if (!aifName)   setAifName(bizProfile.defaultAifName || '');
  // eslint-disable-next-line
  }, [bizProfile, type]);

  const send = async () => {
    setMsg('');
    if (!sellerEmail || !sellerEmail.includes('@')) {
      setMsg('Please enter a valid seller email — that\'s where the signing link will be sent.');
      return;
    }
    setSending(true);
    try {
      const fn = httpsCallable(functions, 'generateAndSendContract');
      const res = await fn({
        leadId: lead.id,
        agentUid,
        contractType: type,
        fields: {
          assetType: lead.assetType === 'land' ? 'land' : 'property',
          subjectProperty: fullAddr,
          legalDescription: lead.propertyData?.legalDescription || 'See legal description in the title commitment attached hereto.',
          purchasePrice: Number(purchasePrice) || 0,
          closingDate, acceptanceDeadline,
          sellerName: (lead.owners || []).map(o => o.name).filter(Boolean).join(' & '),
          sellerEmail: sellerEmail.trim(),
          sellerPhone: lead.owners?.[0]?.phone || '',
          buyerName: buyerName || bizProfile?.defaultBuyerName || 'TBD',
          attorneyInFactName: aifName || bizProfile?.defaultAifName || '',
          assignorName: bizProfile?.defaultBuyerName?.replace(/\s+and\/or assigns$/i, '') || bizProfile?.legalEntityName || 'HomeIQa, LLC',
          assignorAddress: bizProfile?.buyerAddress || '',
          signorName: bizProfile?.legalEntityName || 'HomeIQa, LLC'
        }
      });
      setMsg(`✓ ${res.data?.message || 'Contract emailed for signature.'}`);
    } catch (e) {
      setMsg(`Couldn't send: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const contracts = lead.contracts || [];

  return (
    <Card title="Contracts">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Contract type">
          <Select value={type} onChange={e => setType(e.target.value)}>
            {CONTRACT_TYPES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </Select>
        </Field>
        <Field label="Purchase price ($)"><Input type="number" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} /></Field>
        <Field label="Acceptance deadline"><Input type="date" value={acceptanceDeadline} onChange={e => setAcceptanceDeadline(e.target.value)} /></Field>
        <Field label="Closing date"><Input type="date" value={closingDate} onChange={e => setClosingDate(e.target.value)} /></Field>
        <Field label="Seller email" required hint="The signing link is delivered here.">
          <Input type="email" value={sellerEmail} onChange={e => setSellerEmail(e.target.value)} placeholder="seller@example.com" />
        </Field>
        {type === 'novation' ? (
          <Field
            label="Attorney-in-Fact"
            hint={bizProfile?.defaultAifName ? 'Defaulted from your Business Profile.' : 'Set a default in Settings → Company defaults.'}
          >
            <Input value={aifName} onChange={e => setAifName(e.target.value)} placeholder="HomeIQa, LLC" />
          </Field>
        ) : (
          <Field
            label={type === 'assignment' ? 'Assignee (cash buyer)' : type === 'jv' ? 'JV Partner name' : 'Buyer name'}
            hint={bizProfile?.defaultBuyerName && (type === 'psa' || type === 'cancellation') ? 'Defaulted from your Business Profile.' : null}
          >
            <Input value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder={type === 'assignment' ? 'Cash buyer LLC' : (type === 'jv' ? 'JV partner LLC' : 'HomeIQa, LLC and/or assigns')} />
          </Field>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
        <Btn variant="ghost" onClick={() => previewContract({ type, lead, fullAddr, purchasePrice, closingDate, acceptanceDeadline, buyerName })}>Preview</Btn>
        <Btn onClick={send} disabled={sending || !purchasePrice || !sellerEmail}>{sending ? 'Sending…' : 'Generate & email for signature'}</Btn>
      </div>
      {msg && <div style={s.msgBox}>{msg}</div>}

      {contracts.length > 0 && (
        <ul style={s.contractList}>
          {contracts.map((c, i) => (
            <li key={i} style={s.contractItem}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, color: BURG_DK }}>{CONTRACT_TYPES.find(t => t.id === c.type)?.label || c.type}</div>
                <div style={{ fontSize: 11, color: LIGHT }}>
                  Sent {c.sentAt?.toDate?.()?.toLocaleString?.() || ''}
                  {c.status === 'signed' && c.signedAt?.toDate && <> · ✓ Signed {c.signedAt.toDate().toLocaleString()}</>}
                  {c.signedBy && <> by {c.signedBy}</>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {c.signedPdfUrl && (
                  <a href={c.signedPdfUrl} target="_blank" rel="noreferrer" style={s.pdfLink}>📎 Download PDF</a>
                )}
                <Pill color={c.status === 'signed' ? 'green' : c.status === 'declined' ? 'red' : 'gold'}>{c.status || 'sent'}</Pill>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ─────── Follow-up scheduler ───────
// Pill row of common follow-up windows. Click a pill → confirm modal
// → schedules an SMS via the scheduleFollowUpSms Cloud Function. Shows
// any pending follow-ups for this lead with a Cancel button.
const FOLLOW_UP_OPTIONS = [
  { id: 'tomorrow', label: 'Tomorrow', humanWhen: 'tomorrow', addDays: 1, hour: 10 },
  { id: '3days',    label: 'In 3 days', humanWhen: 'in 3 days', addDays: 3, hour: 10 },
  { id: 'week',     label: 'Next week', humanWhen: 'in 1 week', addDays: 7, hour: 10 },
  { id: '2weeks',   label: 'In 2 weeks', humanWhen: 'in 2 weeks', addDays: 14, hour: 10 },
  { id: 'month',    label: 'In a month', humanWhen: 'in 1 month', addDays: 30, hour: 10 }
];

function FollowUpScheduler({ lead, agentUid, agent, composeBody, ownerName, onScheduled }) {
  const [pendingChoice, setPendingChoice] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [pending, setPending] = useState([]);
  const phone = (lead.owners || [])[0]?.phone || '';
  const ownerUid = profileOwnerUid(lead, agentUid); // pipeline owner — agent or admin

  // Subscribe to this lead's pending follow-ups (mine, or all if admin).
  useEffect(() => {
    if (!agentUid || !lead.id) return;
    const constraints = [where('leadId', '==', lead.id), where('status', '==', 'pending')];
    if (agent?.role !== 'admin') constraints.push(where('agentUid', '==', agentUid));
    const q = query(collection(db, 'scheduledSms'), ...constraints);
    const unsub = onSnapshot(q, snap => {
      setPending(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) =>
        (a.sendAt?.toMillis?.() || 0) - (b.sendAt?.toMillis?.() || 0)
      ));
    }, () => {});
    return unsub;
  }, [lead.id, agentUid, agent?.role]);

  const open = (option) => {
    if (!phone) {
      alert(''); // unreachable — replaced by the inline note below
      return;
    }
    setPendingChoice(option);
  };

  const buildSendAt = (option) => {
    const d = new Date();
    d.setDate(d.getDate() + option.addDays);
    d.setHours(option.hour, 0, 0, 0);
    return d;
  };

  const confirmSchedule = async () => {
    if (!pendingChoice) return;
    setScheduling(true);
    try {
      const sendAt = buildSendAt(pendingChoice);
      const fn = httpsCallable(functions, 'scheduleFollowUpSms');
      const fallbackBody = `Hi${ownerName ? ' ' + ownerName.split(' ')[0] : ''}, just following up on our chat about your property. Still interested in selling?`;
      await fn({
        leadId: lead.id,
        agentUid: ownerUid,
        sendAt: sendAt.getTime(),
        body: (composeBody || '').trim() || fallbackBody,
        ownerPhone: phone
      });
      onScheduled?.(`Follow-up scheduled for ${sendAt.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
      setPendingChoice(null);
    } catch (e) {
      alert(`Couldn't schedule: ${e.message}`); // very rare; keeping inline-friendly fallback
    } finally {
      setScheduling(false);
    }
  };

  const cancel = async (id) => {
    try {
      const fn = httpsCallable(functions, 'cancelFollowUpSms');
      await fn({ id });
    } catch (e) {
      console.error('cancel follow-up failed', e);
    }
  };

  if (!phone) return null;

  return (
    <div style={fu.wrap}>
      <div style={fu.label}>📅 Schedule a follow-up</div>
      <div style={fu.pills}>
        {FOLLOW_UP_OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            onClick={() => open(opt)}
            style={fu.pill}
            title={`Auto-send the SMS ${opt.humanWhen} at 10am`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {pending.length > 0 && (
        <ul style={fu.pendingList}>
          {pending.map(p => (
            <li key={p.id} style={fu.pendingItem}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={fu.pendingTime}>
                  ⏰ Sends {p.sendAt?.toDate?.()?.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || ''}
                </div>
                <div style={fu.pendingBody}>"{(p.body || '').slice(0, 90)}{(p.body || '').length > 90 ? '…' : ''}"</div>
                <div style={fu.pendingMeta}>by {p.agentName || 'Agent'}</div>
              </div>
              <button onClick={() => cancel(p.id)} style={fu.cancelBtn} title="Cancel this follow-up">×</button>
            </li>
          ))}
        </ul>
      )}

      {pendingChoice && (
        <div style={mod.backdrop} onClick={() => !scheduling && setPendingChoice(null)}>
          <div style={mod.panel} onClick={e => e.stopPropagation()}>
            <div style={mod.head}>
              <div style={mod.title}>Confirm follow-up</div>
              <button onClick={() => setPendingChoice(null)} style={mod.x} disabled={scheduling}>×</button>
            </div>
            <div style={{ padding: 22 }}>
              <p style={{ fontSize: 14, color: BURG_DK, lineHeight: 1.6, margin: '0 0 14px' }}>
                Confirm you would like to follow up <strong>{pendingChoice.humanWhen}</strong>?
              </p>
              <div style={{ background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: LIGHT, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6, fontWeight: 500 }}>Will send</div>
                <div style={{ fontSize: 13, color: '#1C1C1C', lineHeight: 1.5, marginBottom: 8 }}>
                  {(composeBody || '').trim() || `Hi${ownerName ? ' ' + ownerName.split(' ')[0] : ''}, just following up on our chat about your property. Still interested in selling?`}
                </div>
                <div style={{ fontSize: 11, color: MID }}>
                  To: <span style={{ fontFamily: 'monospace' }}>{phone}</span> · at <strong>{buildSendAt(pendingChoice).toLocaleString([], { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                </div>
              </div>
              <div style={{ fontSize: 11, color: LIGHT, lineHeight: 1.6 }}>
                We'll automatically text {ownerName ? ownerName.split(' ')[0] : 'the seller'} from your business SMS number on that date. You can cancel any time before it fires.
              </div>
            </div>
            <div style={mod.footer}>
              <Btn variant="ghost" onClick={() => setPendingChoice(null)} disabled={scheduling}>Cancel</Btn>
              <Btn variant="gold" onClick={confirmSchedule} disabled={scheduling}>{scheduling ? 'Scheduling…' : `📅 Schedule follow-up`}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Resolve which agents/{uid} the lead lives under. For admin/contract
// agents the lead is at agents/{my-uid}/leads. The router already routes
// us to the correct lead by leadId so my own uid is the right answer.
function profileOwnerUid(lead, agentUid) {
  return agentUid;
}

const fu = {
  wrap: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
  label: { fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: LIGHT, marginBottom: 6, fontWeight: 500 },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  pill: { background: BURG_DK, color: '#fff', border: 'none', borderRadius: 999, padding: '5px 11px', fontSize: 11, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' },
  pendingList: { listStyle: 'none', padding: 0, margin: '10px 0 0', display: 'flex', flexDirection: 'column', gap: 6 },
  pendingItem: { display: 'flex', alignItems: 'center', gap: 10, background: GOLD_PALE, border: '1px solid rgba(201,168,76,0.4)', borderRadius: 6, padding: '8px 10px' },
  pendingTime: { fontSize: 11, color: BURG_DK, fontWeight: 600 },
  pendingBody: { fontSize: 11, color: '#1C1C1C', fontStyle: 'italic', marginTop: 2 },
  pendingMeta: { fontSize: 10, color: LIGHT, marginTop: 2 },
  cancelBtn: { background: 'transparent', border: '1px solid #FCA5A5', color: '#991B1B', width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }
};

// ─────── SMS thread ───────
// Per-user privacy: by default each contract agent only sees their own
// outbound messages plus any inbound replies that came AFTER one of
// their outbounds. Admin sees everything by default and can switch
// the filter to a specific agent. Contract agents can flip to "All
// agents" if they want context — nothing is hard-blocked, this is a
// UX-level filter for clean inboxes per Ishoun's "you can only see
// your own thread" rule.
function SmsPanel({ lead, agentUid, agent }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  // Default: 'mine' for contract agents, 'all' for admin.
  const [filterAgent, setFilterAgent] = useState(agent?.role === 'admin' ? 'all' : 'mine');
  const phone = (lead.owners || [])[0]?.phone || '';
  const allMessages = (lead.smsThread || []).slice().sort((a, b) =>
    (a.sentAt?.toMillis?.() || 0) - (b.sentAt?.toMillis?.() || 0)
  );
  const agentNames = Array.from(new Set(allMessages.map(m => m.agentName).filter(Boolean)));

  // "mine" filter: my outbound messages PLUS inbound messages that
  // arrived after one of my outbounds (i.e. likely replies to me).
  const myMessages = useMemo(() => {
    const out = [];
    let lastWasMine = false;
    for (const m of allMessages) {
      if (m.direction === 'out') {
        const mine = m.agentUid === agentUid || m.agentName === agent?.name;
        if (mine) { out.push(m); lastWasMine = true; }
        else { lastWasMine = false; }
      } else {
        // inbound — claim it if the most recent prior outbound was mine
        if (lastWasMine) out.push(m);
      }
    }
    return out;
  }, [allMessages, agentUid, agent?.name]);

  const messages =
    filterAgent === 'all'  ? allMessages :
    filterAgent === 'mine' ? myMessages  :
    allMessages.filter(m => m.direction === 'in' || m.agentName === filterAgent);

  // Auto-scroll the SMS thread to the bottom whenever new messages
  // arrive, so the latest exchange is always in view without the
  // agent having to scroll inside the list manually.
  const smsListRef = useRef(null);
  useEffect(() => {
    const el = smsListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!body.trim() || !phone) return;
    setSending(true); setMsg('');
    try {
      const fn = httpsCallable(functions, 'sendLeadSms');
      const agentFirst = (agent?.name || '').split(/\s+/).find(p => p.length > 1 && !p.endsWith('.')) || agent?.name || '';
      const res = await fn({ leadId: lead.id, to: phone, body, agentName: agentFirst });
      setMsg(`✓ ${res.data?.message || 'SMS sent.'}`);
      setBody('');
    } catch (e) {
      const m = e.message || '';
      let hint = '';
      if (/A2P/i.test(m) || /not approved/i.test(m)) {
        hint = '\n→ The phone number needs carrier (A2P 10DLC) approval before SMS can be sent. Voice calls still work in the meantime. Contact your admin.';
      } else if (/not configured/i.test(m)) {
        hint = '\n→ SMS is not configured yet. Contact your admin.';
      }
      setMsg(`Couldn't send SMS: ${m}${hint}`);
    } finally {
      setSending(false);
    }
  };

  // Template context for interpolation
  const ctx = {
    agentName: (agent?.name || '').split(/\s+/).find(p => p.length > 1 && !p.endsWith('.')) || agent?.name || '',
    ownerFirstName: ownerFirstName(lead.owners),
    city: lead.address?.city || '',
    state: lead.address?.state || '',
    streetName: streetNameOnly(lead.address?.line1)
  };
  const introSent = !!lead._introSmsSent;
  const fillTemplate = (tpl) => setBody(interpolateTemplate(tpl.body, ctx));

  return (
    <Card title="SMS thread" right={
      <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)} style={{ fontSize: 11, padding: '4px 8px', border: `1px solid ${BORDER}`, borderRadius: 4, fontFamily: "'DM Sans', sans-serif", color: BURG_DK }}>
        <option value="mine">My conversation only</option>
        {(agent?.role === 'admin' || agentNames.length > 1) && <option value="all">All agents</option>}
        {agent?.role === 'admin' && agentNames.filter(n => n !== agent?.name).map(n => <option key={n} value={n}>{n}'s only</option>)}
      </select>
    }>
      <div style={{ fontSize: 11, color: LIGHT, marginBottom: 10 }}>
        To: <span style={{ fontFamily: 'monospace', color: BURG_DK }}>{phone || '—'}</span>
      </div>

      {/* Quick-send template pills */}
      <div style={s.tplWrap}>
        <div style={s.tplLabel}>Quick send</div>
        <div style={s.tplPills}>
          {SMS_TEMPLATES.map(t => {
            const isIntro = t.id === 'intro';
            const pulse = isIntro && !introSent;
            const sent = isIntro && introSent;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => fillTemplate(t)}
                title={t.description}
                style={{
                  ...s.tplPill,
                  ...(pulse ? s.tplPillPulse : {}),
                  ...(sent ? s.tplPillSent : {})
                }}
              >
                {sent && <span style={{ marginRight: 4 }}>✓</span>}
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <FollowUpScheduler
        lead={lead}
        agentUid={agentUid}
        agent={agent}
        composeBody={body}
        ownerName={lead.owners?.[0]?.name || ''}
        onScheduled={(msg) => { setMsg(`✓ ${msg}`); setBody(''); }}
      />
      {messages.length === 0 ? (
        <div style={{ fontSize: 12, color: LIGHT, padding: '10px 0' }}>No messages yet.</div>
      ) : (
        <ul ref={smsListRef} style={s.smsList}>
          {messages.map((m, i) => (
            <li key={i} style={{ ...s.smsBubble, ...(m.direction === 'in' ? s.smsIn : s.smsOut), ...(m.deliveryStatus === 'failed' ? s.smsFailed : {}) }}>
              <div>{m.body}</div>
              <div style={s.smsMeta}>
                {m.direction === 'out' && m.agentName ? `${m.agentName} · ` : ''}
                {m.sentAt?.toDate?.()?.toLocaleString?.() || ''}
                {m.direction === 'out' && m.deliveryStatus === 'delivered' && <span style={s.smsDelivered}> · ✓ Delivered</span>}
                {m.direction === 'out' && m.deliveryStatus === 'failed' && <span style={s.smsFailedTag}> · ⚠️ Not delivered{m.deliveryError ? ` (${m.deliveryError})` : ''}</span>}
              </div>
              {m.direction === 'out' && m.deliveryStatus === 'failed' && (
                <button type="button" onClick={() => setBody(m.body)} style={s.smsResend} title="Copy this message back into the box to resend">
                  ↻ Resend this message
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <TextArea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="Type a message…"
        rows={3}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 10, color: LIGHT }}>{body.length} / 320 · STOP to opt-out</span>
        <Btn onClick={send} disabled={sending || !body.trim() || !phone}>{sending ? 'Sending…' : 'Send SMS'}</Btn>
      </div>
      {msg && <div style={s.msgBox}>{msg}</div>}
    </Card>
  );
}

// ─────── AI assistant ───────
function AiPanel({ lead, fullAddr }) {
  const [task, setTask] = useState('cold-call');
  const [out, setOut] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setOut('');
    try {
      const fn = httpsCallable(functions, 'aiAssist');
      const res = await fn({
        task,
        lead: {
          id: lead.id,
          address: fullAddr,
          owner: (lead.owners || [])[0]?.name || '',
          status: lead.status,
          notes: (lead.notes || []).slice(-3).map(n => `${n.type}: ${n.body}`).join('\n'),
          calc: lead.calc?.results || null,
          propertyData: lead.propertyData || null
        }
      });
      setOut(res.data?.text || JSON.stringify(res.data, null, 2));
    } catch (e) {
      setOut(`AI not connected yet: ${e.message}\n→ Add OPENAI_API_KEY in Cloud Functions to enable.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="AI assistant">
      <div style={{ display: 'flex', gap: 10, alignItems: 'end' }}>
        <Field label="Task" >
          <Select value={task} onChange={e => setTask(e.target.value)}>
            <option value="cold-call">Cold-call openings & objections</option>
            <option value="repair-estimate">Repair cost estimate</option>
            <option value="arv-analysis">ARV / comp analysis</option>
            <option value="follow-up-sms">Draft follow-up SMS</option>
          </Select>
        </Field>
        <Btn onClick={run} disabled={loading}>{loading ? 'Thinking…' : 'Run'}</Btn>
      </div>
      {out && <pre style={s.aiOut}>{out}</pre>}
    </Card>
  );
}

function previewContract({ type, lead, fullAddr, purchasePrice, closingDate, acceptanceDeadline, buyerName }) {
  const fields = {
    sellerName: (lead.owners || []).map(o => o.name).filter(Boolean).join(' & '),
    sellerAddress: fullAddr,
    buyerName: buyerName || 'TBD and/or assigns',
    subjectProperty: fullAddr,
    legalDescription: lead.propertyData?.legalDescription || 'See legal description in the title commitment attached hereto.',
    purchasePrice: Number(purchasePrice) || 0,
    acceptanceDeadline,
    closingDate,
    // Assignment-specific defaults derived from the lead so preview is meaningful
    assignmentDate: new Date().toISOString().slice(0, 10),
    assignorName: 'HomeIQa Investments LLC',
    assignorAddress: '',
    assigneeName: buyerName || '',
    assigneeAddress: '',
    earnestMoneyAmount: 1000,
    titleCompany: '',
    // Cancellation
    cancellationDate: new Date().toISOString().slice(0, 10),
    reasonForTermination: '',
    executionDate: new Date().toISOString().slice(0, 10),
    // Novation
    attorneyInFactName: '',
    // JV
    jvDate: new Date().toISOString().slice(0, 10),
    signorName: 'HomeIQa Investments LLC',
    partnerName: '',
    splitLabel: '50/50',
    signorPct: 50, partnerPct: 50,
    propertyAddress: fullAddr,
    additionalConditions: ''
  };
  const html = renderContractHtml({
    type,
    fields,
    assetType: lead.assetType === 'land' ? 'land' : 'property',
    documentId: 'PREVIEW-' + (lead.id ? lead.id.slice(0, 8).toUpperCase() : 'XXXX'),
    parties: CONTRACT_META[type].signers.map(p => ({
      ...p,
      name: p.role === 'seller' ? fields.sellerName :
            p.role === 'buyer'  ? fields.buyerName  :
            p.role === 'assignor' ? fields.assignorName :
            p.role === 'assignee' ? fields.assigneeName :
            p.role === 'signor'  ? fields.signorName :
            p.role === 'partner' ? fields.partnerName : ''
    }))
  });
  const w = window.open('', '_blank', 'width=1000,height=900');
  if (!w) { alert('Pop-ups blocked — please allow pop-ups to preview the contract.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

const s = {
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' },
  kvGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 12 },
  tip: { background: GOLD_PALE, borderLeft: `3px solid ${GOLD}`, padding: '10px 14px', fontSize: 12, color: BURG_DK, borderRadius: '0 4px 4px 0' },
  calcResults: { marginTop: 16, paddingTop: 14, borderTop: `1px solid ${BORDER}` },
  resRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0' },
  resRowHi: { background: '#F9FAFB', padding: '14px 16px', borderRadius: 10, marginBottom: 6 },
  resLabel: { flex: 1, fontSize: 13, color: MID, fontWeight: 500, fontFamily: "'Inter', sans-serif" },
  resValue: { fontFamily: "'Manrope', sans-serif", fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' },
  formulaNote: { fontSize: 12, color: LIGHT, marginTop: 10, fontFamily: "'Inter', sans-serif" },
  formulaPreview: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: BURG_DK, color: '#fff', borderRadius: 10, fontFamily: "'Manrope', sans-serif", fontSize: 15, fontWeight: 600, flexWrap: 'wrap', letterSpacing: '-0.01em' },
  formulaTerm: { color: GOLD_PALE },
  formulaOp: { color: GOLD, fontSize: 18 },
  formulaResult: { color: GOLD, fontSize: 22, fontWeight: 800, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' },
  arvSuggestion: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 12, marginBottom: 16 },
  arvSuggestionLabel: { fontSize: 11, color: '#7A5D1A', marginBottom: 4, fontWeight: 500, fontFamily: "'Inter', sans-serif" },
  arvSuggestionValue: { fontFamily: "'Manrope', sans-serif", fontSize: 26, fontWeight: 800, color: BURG_DK, lineHeight: 1.1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
  commissionRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 8, marginTop: 14, flexWrap: 'wrap' },
  notesList: { listStyle: 'none', padding: 0, marginTop: 14 },
  note: { padding: '12px 0', borderBottom: `1px solid ${BORDER}` },
  noteHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 },
  noteMeta: { fontSize: 10, color: LIGHT },
  noteBody: { fontSize: 13, color: '#1C1C1C', lineHeight: 1.55, whiteSpace: 'pre-wrap' },
  msgBox: { background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 4, padding: '10px 14px', fontSize: 12, color: BURG_DK, marginTop: 10, whiteSpace: 'pre-wrap' },
  editSection: { fontSize: 13, fontWeight: 600, color: '#111827', paddingTop: 14, paddingBottom: 8, borderBottom: `1px solid ${BORDER}`, marginBottom: 12, fontFamily: "'Inter', sans-serif" },
  removeOwnerBtn: { width: 32, height: 38, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, cursor: 'pointer', color: LIGHT, fontSize: 16, marginBottom: 14 },
  addOwnerBtn: { background: 'transparent', border: 'none', color: BURG, fontSize: 12, padding: '4px 0', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginBottom: 8 },
  contractList: { listStyle: 'none', padding: 0, marginTop: 14 },
  contractItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${BORDER}`, gap: 12 },
  pdfLink: { fontSize: 11, color: BURG, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${BORDER}`, borderRadius: 999, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, whiteSpace: 'nowrap' },
  smsList: { listStyle: 'none', padding: 0, marginBottom: 10, maxHeight: 500, overflowY: 'auto' },
  tplWrap: { background: '#F9FAFB', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 },
  tplLabel: { fontSize: 11, color: LIGHT, marginBottom: 8, fontWeight: 500, fontFamily: "'Inter', sans-serif" },
  tplPills: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tplPill: { background: '#fff', border: `1px solid #D1D5DB`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Inter', sans-serif", transition: 'all 0.15s ease' },
  tplPillPulse: { background: GOLD, color: BURG_DK, border: `1px solid ${GOLD}`, fontWeight: 600, animation: 'hiqaPulse 1.6s ease-in-out infinite' },
  tplPillSent: { background: GOLD_PALE, color: BURG_DK, borderColor: 'rgba(201,168,76,0.5)' },
  useAvmBtn: { marginLeft: 8, background: 'transparent', border: `1px solid #D1D5DB`, borderRadius: 8, padding: '3px 10px', fontSize: 11, color: BURG, cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontWeight: 500 },
  smsBubble: { padding: '10px 14px', borderRadius: 18, marginBottom: 8, maxWidth: '78%', fontSize: 14, lineHeight: 1.4, fontFamily: "'Inter', sans-serif", wordBreak: 'break-word' },
  smsOut: { background: BURG, color: '#fff', marginLeft: 'auto', borderBottomRightRadius: 6 },
  smsIn: { background: '#F3F4F6', color: '#111827', borderBottomLeftRadius: 6 },
  smsFailed: { background: '#991B1B', border: 'none' },
  smsMeta: { fontSize: 10, opacity: 0.7, marginTop: 4, fontFamily: "'Inter', sans-serif" },
  smsDelivered: { opacity: 0.85 },
  smsFailedTag: { color: '#FECACA', fontWeight: 700, opacity: 1 },
  smsResend: { marginTop: 6, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 999, padding: '3px 12px', fontSize: 11, cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontWeight: 500 },
  aiOut: { background: '#111827', color: '#F3F4F6', borderRadius: 10, padding: 16, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowX: 'auto', marginTop: 14, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }
};
